import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { NavIcon, RightDrawer, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth, usePermissions } from '@helios/auth-firebase-web';
import { PERM } from '../../../auth/permissionMap';
import {
  useVoicePrescription,
  type UseVoicePrescriptionReturn,
} from '../../../hooks/useVoicePrescription';
import {
  qAppConfig,
  qAppointmentProviders,
  qInventoryPickerCandidates,
  qPatientAllergies,
  qPatientById,
  qPatientContact,
  qPrescriptionPrintSettings,
  qPrescriptionsByEncounter,
  qPrescriptionTemplateById,
  qPrescriptionTemplateItems,
  qPrescriptionTemplates,
  qUserProviderLink,
  qVitalsByEncounterId,
  qEncounterById,
  qLabOrdersForEncounter,
  isLaboratoryViewAvailable,
  selectVisibleLabOrders,
  pickerCandidateRowToSelection,
  toPrescriptionLineSnapshot,
  resolveVoiceMedications,
  extractedMedicationToDraftFields,
  resolvePrintLetterhead,
  formatVitalsSummary,
  buildPrintVitals,
  findAllergyConflicts,
  highestSeverity,
  conflictAckKey,
  type AppConfigRow,
  type AppointmentProviderRow,
  type InventoryPickerCandidateRow,
  type InventoryPickerSelection,
  type PatientAllergyRow,
  type PatientContactRow,
  type PatientDetailRow,
  type PatientEncounterRow,
  type PatientPrescriptionRow,
  type PatientVitalEntryWithDefinitionRow,
  type PrescriptionPrintSettingsRow,
  type PrescriptionTemplateItemRow,
  type PrescriptionTemplateListRow,
  type PrescriptionTemplateRow,
  type UserProviderLinkRow,
  type LabOrderRow,
  type AllergyConflict,
  type AllergySeverity,
  type ExtractedMedication,
  type InventoryMatchCandidate,
  applyPrintPurpose,
} from '@helios/data-access';
import {
  PrescriptionPrintLayout,
  type PrintPrescriptionItem,
} from '../../clinic/prescription-print/PrescriptionPrintLayout';
import { useEncounterPrintClinical } from '../../clinic/prescription-print/useEncounterPrintClinical';
import {
  formatPatientAge,
  printImageDataUrl,
  resolveDoctor,
  resolvePrintSettings,
} from '../../clinic/prescription-print/print-utils';
import { SharePrescriptionModal } from '../../../components/SharePrescriptionModal';
import { EncounterLabOrdersGroup } from './EncounterLabOrdersGroup';
import { useLaboratoryCapabilities } from '../../../hooks/useLaboratory';
import { PrintProgressToast } from '../../../components/PrintProgressToast';
import { renderNodeToPng } from '../../../lib/prescription-image';

// ─── Constants ───

/** PRD.305 — outline CTA shared by the canvas empty state's two add actions. */
const emptyStateCtaStyle = (primary: string): CSSProperties => ({
  height: 32,
  padding: '0 14px',
  borderRadius: 'var(--radius-md)',
  border: `1px solid ${primary}`,
  background: 'transparent',
  color: primary,
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  fontFamily: 'inherit',
});

const INTAKE_TIMING_OPTIONS = [
  { value: '', label: 'Select timing…' },
  { value: 'before_food', label: 'Before Food' },
  { value: 'after_food', label: 'After Food' },
  { value: 'with_food', label: 'With Food' },
  { value: 'empty_stomach', label: 'Empty Stomach' },
  { value: 'anytime', label: 'Anytime' },
] as const;

const DURATION_UNIT_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
] as const;

// ─── Draft (in-memory editable row) ───

interface PrescriptionDraft {
  id: string;
  isNew: boolean;
  itemName: string;
  itemDetails: string;
  intakeTiming: string;
  doseQuantity: string;
  scheduleMorning: boolean;
  scheduleAfternoon: boolean;
  scheduleNight: boolean;
  durationValue: string; // input as string for empty-state ergonomics
  durationUnit: string;
  instructions: string;
  sortOrder: number;

  // PRD.189 — optional Medicine inventory reference + snapshots.
  // When inventoryItemId is set, snapshot fields hold the catalog values
  // captured at the time of selection. Free-text-only lines leave
  // inventoryItemId null and may set freeTextName.
  inventoryItemId: string | null;
  freeTextName: string | null;
  itemNameSnapshot: string | null;
  skuSnapshot: string | null;
  genericNameSnapshot: string | null;
  brandNameSnapshot: string | null;
  strengthSnapshot: string | null;
  dosageFormSnapshot: string | null;
  routeSnapshot: string | null;
  unitSnapshot: string | null;
  defaultInstructionSnapshot: string | null;
  defaultDosageSnapshot: string | null;
  usageSnapshot: string | null;
  doseSnapshot: string | null;
  doseUnitSnapshot: string | null;
  frequencySnapshot: string | null;
  durationSnapshot: string | null;

  // PRD.219 — origin template id when a row was applied from a prescription
  // template. Persists through edits so saved rows retain provenance.
  sourceTemplateId: string | null;

  // PRD.217 — provenance + lifecycle.
  // source: 'ai' | 'clinician' | 'template' | null (null treated as clinician)
  // status: 'draft' | 'final' | 'cancelled' (draft = AI suggestion awaiting
  //   clinician review; excluded from print and billing)
  source: string | null;
  status: string;
}

function rowToDraft(row: PatientPrescriptionRow): PrescriptionDraft {
  return {
    id: row.id,
    isNew: false,
    itemName: row.item_name ?? '',
    itemDetails: row.item_details ?? '',
    intakeTiming: row.intake_timing ?? '',
    doseQuantity: row.dose_quantity ?? '',
    scheduleMorning: row.schedule_morning === 1,
    scheduleAfternoon: row.schedule_afternoon === 1,
    scheduleNight: row.schedule_night === 1,
    durationValue: row.duration_value != null ? String(row.duration_value) : '',
    durationUnit: row.duration_unit ?? 'days',
    instructions: row.instructions ?? '',
    sortOrder: row.sort_order ?? 0,
    inventoryItemId: row.inventory_item_id,
    freeTextName: row.free_text_name,
    itemNameSnapshot: row.item_name_snapshot,
    skuSnapshot: row.sku_snapshot,
    genericNameSnapshot: row.generic_name_snapshot,
    brandNameSnapshot: row.brand_name_snapshot,
    strengthSnapshot: row.strength_snapshot,
    dosageFormSnapshot: row.dosage_form_snapshot,
    routeSnapshot: row.route_snapshot,
    unitSnapshot: row.unit_snapshot,
    defaultInstructionSnapshot: row.default_instruction_snapshot,
    defaultDosageSnapshot: row.default_dosage_snapshot,
    usageSnapshot: row.usage_snapshot,
    doseSnapshot: row.dose_snapshot,
    doseUnitSnapshot: row.dose_unit_snapshot,
    frequencySnapshot: row.frequency_snapshot,
    durationSnapshot: row.duration_snapshot,
    sourceTemplateId: row.source_template_id,
    source: row.source,
    status: row.status ?? 'final',
  };
}

function emptyDraft(sortOrder: number): PrescriptionDraft {
  return {
    id: crypto.randomUUID(),
    isNew: true,
    itemName: '',
    itemDetails: '',
    intakeTiming: '',
    doseQuantity: '',
    scheduleMorning: false,
    scheduleAfternoon: false,
    scheduleNight: false,
    durationValue: '',
    durationUnit: 'days',
    instructions: '',
    sortOrder,
    inventoryItemId: null,
    freeTextName: null,
    itemNameSnapshot: null,
    skuSnapshot: null,
    genericNameSnapshot: null,
    brandNameSnapshot: null,
    strengthSnapshot: null,
    dosageFormSnapshot: null,
    routeSnapshot: null,
    unitSnapshot: null,
    defaultInstructionSnapshot: null,
    defaultDosageSnapshot: null,
    usageSnapshot: null,
    doseSnapshot: null,
    doseUnitSnapshot: null,
    frequencySnapshot: null,
    durationSnapshot: null,
    sourceTemplateId: null,
    source: null,
    status: 'final',
  };
}

// Build "Brand · Generic · Strength · Form" from a picker selection for the
// item-details column. Falls back to '' if all fields are blank.
function buildItemDetailsFromSelection(s: InventoryPickerSelection): string {
  return [s.brandName, s.genericName, s.strength, s.dosageForm].filter(Boolean).join(' · ');
}

// PRD.280 — all-null snapshot fields for a voice draft with no inventory link
// (unmatched / ambiguous). Shape matches the `*_snapshot` columns read by the
// voice INSERT; a matched selection overwrites it with real catalog values.
function emptyLineSnapshot() {
  return {
    item_name_snapshot: null as string | null,
    sku_snapshot: null as string | null,
    generic_name_snapshot: null as string | null,
    brand_name_snapshot: null as string | null,
    strength_snapshot: null as string | null,
    dosage_form_snapshot: null as string | null,
    route_snapshot: null as string | null,
    unit_snapshot: null as string | null,
    default_instruction_snapshot: null as string | null,
    default_dosage_snapshot: null as string | null,
    usage_snapshot: null as string | null,
    dose_snapshot: null as string | null,
    dose_unit_snapshot: null as string | null,
    frequency_snapshot: null as string | null,
    duration_snapshot: null as string | null,
  };
}

// Apply a picker selection onto a draft: bind the inventory item, populate
// snapshot fields, and prefill display/dosage/instruction fields the
// prescriber may still edit. Existing prescriber edits are preserved when
// the corresponding inventory field is empty.
function applyPickerSelection(
  draft: PrescriptionDraft,
  selection: InventoryPickerSelection,
): PrescriptionDraft {
  const next: PrescriptionDraft = {
    ...draft,
    inventoryItemId: selection.inventoryItemId,
    freeTextName: null,
    // Prefill display fields (overwrite — picking an item is an explicit
    // action). The prescriber can still edit each cell after selection.
    itemName: selection.itemName,
    itemDetails: buildItemDetailsFromSelection(selection),
    // Snapshot all candidate fields
    itemNameSnapshot: selection.itemName,
    skuSnapshot: selection.sku,
    genericNameSnapshot: selection.genericName,
    brandNameSnapshot: selection.brandName,
    strengthSnapshot: selection.strength,
    dosageFormSnapshot: selection.dosageForm,
    routeSnapshot: selection.route,
    unitSnapshot: selection.inventoryUnit,
    defaultInstructionSnapshot: selection.defaultInstruction,
    defaultDosageSnapshot: selection.defaultDosage,
    usageSnapshot: selection.usage,
    doseSnapshot: selection.dose,
    doseUnitSnapshot: selection.doseUnit,
    frequencySnapshot: selection.frequency,
    durationSnapshot: selection.duration,
  };
  // Prefill dosage / instructions only when the prescriber hasn't typed
  // anything yet (avoid stomping in-progress edits).
  if (!draft.doseQuantity.trim() && selection.defaultDosage) {
    next.doseQuantity = selection.defaultDosage;
  }
  if (!draft.instructions.trim() && selection.defaultInstruction) {
    next.instructions = selection.defaultInstruction;
  }
  return next;
}

// Free-text mode: clear inventory binding + snapshots; preserve typed text.
function clearInventoryBinding(draft: PrescriptionDraft): PrescriptionDraft {
  return {
    ...draft,
    inventoryItemId: null,
    freeTextName: draft.itemName.trim() || null,
    itemNameSnapshot: null,
    skuSnapshot: null,
    genericNameSnapshot: null,
    brandNameSnapshot: null,
    strengthSnapshot: null,
    dosageFormSnapshot: null,
    routeSnapshot: null,
    unitSnapshot: null,
    defaultInstructionSnapshot: null,
    defaultDosageSnapshot: null,
    usageSnapshot: null,
    doseSnapshot: null,
    doseUnitSnapshot: null,
    frequencySnapshot: null,
    durationSnapshot: null,
  };
}

// ─── Props ───

interface EncounterPrescriptionsTabProps {
  patientId: string;
  encounterId: string;
  visitId?: string | null;
  hideHeader?: boolean;
  quietActions?: boolean;
}

// ─── Component ───

export function EncounterPrescriptionsTab({
  patientId,
  encounterId,
  visitId,
  hideHeader,
  quietActions,
}: EncounterPrescriptionsTabProps) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const userName = user?.displayName ?? user?.email ?? '';

  const { data: rows = [] } = usePowerSyncQuery<PatientPrescriptionRow>(
    qPrescriptionsByEncounter(encounterId),
  );

  // Allergy safety check — the patient's documented allergies, matched against
  // each prescribed line so we can warn before prescribing an allergen.
  const { data: allergyRows = [] } = usePowerSyncQuery<PatientAllergyRow>(
    qPatientAllergies(patientId),
  );

  // ── Print Prescription (PRD.184) ──
  // The action uses the clinic's saved org-default print settings, falling
  // back to app_config branding when the wizard hasn't been run yet, and
  // resolves the printing user's doctor identity from their linked provider.
  const { data: printSettingsRows = [] } = usePowerSyncQuery<PrescriptionPrintSettingsRow>(
    qPrescriptionPrintSettings(),
  );
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const { data: providerLinkRows = [] } = usePowerSyncQuery<UserProviderLinkRow>(
    qUserProviderLink(orgId, userId),
  );
  const { data: providerRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: patientRows = [] } = usePowerSyncQuery<PatientDetailRow>(qPatientById(patientId));
  // PRD.238 — patient contact for the WhatsApp number shown in the share dialog.
  const { data: contactRows = [] } = usePowerSyncQuery<PatientContactRow>(
    qPatientContact(patientId),
  );
  // PRD.284 — encounter vitals + chief complaint for the printed prescription
  // (rendered only when the print settings' Vitals / Complaint sections are on).
  const { data: vitalsRows = [] } = usePowerSyncQuery<PatientVitalEntryWithDefinitionRow>(
    qVitalsByEncounterId(encounterId),
  );
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncounterById(encounterId),
  );

  const printSettings = useMemo(
    () =>
      // PRD.327 — everything this tab produces IS a prescription: Print,
      // Download and the shared image alike. So the prescription purpose is
      // applied once, here, and the Clinical Note, Vitals and Chief Complaint
      // are suppressed at runtime. Consultation detail is printed from the
      // Visit Note menu's Consultation Sheet action instead.
      //
      // Runtime only — the clinic's saved settings are never written to.
      applyPrintPurpose(
        resolvePrintSettings(printSettingsRows[0] ?? null, appConfigRows[0] ?? null),
        'prescription',
      ),
    [printSettingsRows, appConfigRows],
  );
  const printVitalsSummary = useMemo(() => formatVitalsSummary(vitalsRows), [vitalsRows]);
  const printComplaint = encounterRows[0]?.chief_complaint ?? '';
  // PRD.320 — Dental Structured clinical content, composed from canonical
  // encounter domains by the SAME hook every other print path uses.
  const printVitals = useMemo(() => buildPrintVitals(vitalsRows), [vitalsRows]);
  const printClinical = useEncounterPrintClinical(
    encounterId ?? '',
    patientId ?? '',
    printComplaint,
  );
  // PRD.284 — clinic-wide print font color + letterhead branding.
  const printBranding = useMemo(
    () => resolvePrintLetterhead(appConfigRows[0] ?? null),
    [appConfigRows],
  );
  const printDoctor = useMemo(() => {
    const linkedProviderId = providerLinkRows[0]?.provider_id ?? null;
    const linkedProvider = linkedProviderId
      ? (providerRows.find((p) => p.id === linkedProviderId) ?? null)
      : null;
    return resolveDoctor(linkedProvider, user?.displayName ?? null);
  }, [providerLinkRows, providerRows, user?.displayName]);

  const printPatient = useMemo(() => {
    const p = patientRows[0];
    return {
      name: p?.full_name ?? '',
      age: formatPatientAge(p), // PRD.315 — falls back to reported age
      sex: p?.sex ?? '',
    };
  }, [patientRows]);

  const printItems: PrintPrescriptionItem[] = useMemo(
    () =>
      // PRD.189 — prefer snapshot fields when present so historical
      // prescriptions render correctly even if the inventory item
      // was edited or deactivated later.
      // PRD.217 §12 — exclude `status='draft'` rows (AI suggestions awaiting
      // clinician review). Old rows with null status backfill to 'final'.
      rows
        .filter((r) => (r.status ?? 'final') === 'final')
        .map((r) => {
          const snapshotDetails = [
            r.brand_name_snapshot,
            r.generic_name_snapshot,
            r.strength_snapshot,
            r.dosage_form_snapshot,
          ]
            .filter(Boolean)
            .join(' · ');
          return {
            itemName:
              r.item_name_snapshot ?? r.free_text_name ?? r.item_name ?? 'Untitled medicine',
            itemDetails: r.item_details ?? snapshotDetails,
            intakeTiming: r.intake_timing ?? '',
            doseQuantity: r.dose_quantity ?? r.default_dosage_snapshot ?? '',
            scheduleMorning: r.schedule_morning === 1,
            scheduleAfternoon: r.schedule_afternoon === 1,
            scheduleNight: r.schedule_night === 1,
            durationValue: r.duration_value ?? null,
            durationUnit: r.duration_unit ?? '',
            instructions: r.instructions ?? r.default_instruction_snapshot ?? '',
          };
        }),
    [rows],
  );

  // Offscreen A4 node captured by html2canvas — shared by Print (rasterize →
  // print the image) and Download (rasterize → save PNG).
  const downloadRef = useRef<HTMLDivElement | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  // Same feedback the Visit Note print actions show: rasterizing an A4 sheet
  // takes long enough that a merely-disabled button reads as "nothing
  // happened".
  const [printError, setPrintError] = useState<string | null>(null);

  const handlePrint = useCallback(async () => {
    // Rasterize the offscreen prescription node to a PNG and print THAT image
    // (not live HTML). A self-contained bitmap re-renders identically when the
    // iPad print sheet reloads on a settings change — the live-HTML iframe goes
    // blank in that case (fonts/logo/DOM re-render inconsistently).
    if (!downloadRef.current || printing) return;
    setPrinting(true);
    setPrintError(null);
    try {
      const png = await renderNodeToPng({ node: downloadRef.current });
      if (png.dataUrl) printImageDataUrl(png.dataUrl, 'Prescription');
      else setPrintError('Could not prepare this prescription. Please try again.');
    } catch (err) {
      console.error('[prescription-print] image render failed', err);
      setPrintError('Could not prepare this prescription. Please try again.');
    } finally {
      // Rasterizing is the slow part; once the dialog is up the toast has done
      // its job and the button returns to normal.
      setPrinting(false);
    }
  }, [printing]);

  // Drafts for newly added rows that haven't been saved yet (no DB row exists).
  const [pendingDrafts, setPendingDrafts] = useState<PrescriptionDraft[]>([]);
  const [editingDrafts, setEditingDrafts] = useState<Record<string, PrescriptionDraft>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const savedDrafts: PrescriptionDraft[] = useMemo(
    () => rows.map((r) => editingDrafts[r.id] ?? rowToDraft(r)),
    [rows, editingDrafts],
  );

  const allDrafts: PrescriptionDraft[] = useMemo(
    () => [...savedDrafts, ...pendingDrafts],
    [savedDrafts, pendingDrafts],
  );

  // Allergy conflicts per line. A cancelled line is ignored — it isn't being
  // prescribed. Empty map when the patient has no documented allergies.
  const conflictsByDraftId = useMemo(() => {
    const m = new Map<string, AllergyConflict[]>();
    if (allergyRows.length === 0) return m;
    for (const d of allDrafts) {
      if (d.status === 'cancelled') continue;
      const conflicts = findAllergyConflicts(d, allergyRows);
      if (conflicts.length > 0) m.set(d.id, conflicts);
    }
    return m;
  }, [allDrafts, allergyRows]);

  // Distinct conflicting lines for the summary banner above the grid.
  const conflictSummary = useMemo(() => {
    const substances = new Map<string, AllergySeverity>();
    for (const conflicts of conflictsByDraftId.values()) {
      for (const c of conflicts) {
        const worst = substances.get(c.substance);
        if (!worst || c.severity === 'severe') substances.set(c.substance, c.severity);
      }
    }
    return {
      lineCount: conflictsByDraftId.size,
      items: [...substances.entries()].map(([substance, severity]) => ({ substance, severity })),
    };
  }, [conflictsByDraftId]);

  // Detect dirty state for saved rows (so we know when to show Save button)
  const isRowDirty = useCallback(
    (draft: PrescriptionDraft): boolean => {
      if (draft.isNew) return true;
      const existing = rows.find((r) => r.id === draft.id);
      if (!existing) return true;
      const persisted = rowToDraft(existing);
      return JSON.stringify(persisted) !== JSON.stringify(draft);
    },
    [rows],
  );

  const updateDraft = useCallback(
    (id: string, patch: Partial<PrescriptionDraft>) => {
      // Pending (unsaved) row?
      const pendingIdx = pendingDrafts.findIndex((d) => d.id === id);
      if (pendingIdx >= 0) {
        setPendingDrafts((prev) => prev.map((d, i) => (i === pendingIdx ? { ...d, ...patch } : d)));
        return;
      }
      // Saved row — track edits in editingDrafts
      const existing = rows.find((r) => r.id === id);
      if (!existing) return;
      const base = editingDrafts[id] ?? rowToDraft(existing);
      setEditingDrafts((prev) => ({ ...prev, [id]: { ...base, ...patch } }));
    },
    [pendingDrafts, rows, editingDrafts],
  );

  const handleAddRow = useCallback(() => {
    const sortOrder = allDrafts.length;
    setPendingDrafts((prev) => [...prev, emptyDraft(sortOrder)]);
  }, [allDrafts.length]);

  // ─── PRD.280 — Voice-assisted prescription entry ───
  // The mic gate reuses the existing clinical-extraction run permission (same
  // Siksha AI provider family) plus the voice + medication-extraction endpoints
  // being configured (resolved inside the hook). Fails closed.
  const { canAccess } = usePermissions();
  const canVoicePrescribe = canAccess(PERM.clinicalExtractionRun);

  // Latest sort base for appended voice rows, read inside the async apply
  // callback without stale closure over `allDrafts`.
  const sortBaseRef = useRef(0);
  sortBaseRef.current = allDrafts.length;

  // Apply extracted medications: match inventory on the client (same candidate
  // list + snapshot mapping as manual entry), then INSERT one editable draft
  // row (source='ai', status='draft') per valid medication. Never replaces
  // existing rows; the allergy/interaction/Accept lifecycle then applies as for
  // any AI draft. Returns the number of items added.
  const applyVoiceMedications = useCallback(
    async (medications: ExtractedMedication[], _meta: { requestId?: string }): Promise<number> => {
      if (!db || medications.length === 0) return 0;

      // Reuse the manual picker candidate scope (medicine + product, active).
      const spec = qInventoryPickerCandidates('', ['medicine', 'product']);
      let candidateRows: InventoryPickerCandidateRow[] = [];
      try {
        candidateRows = await db.getAll<InventoryPickerCandidateRow>(
          spec.sql,
          spec.params as unknown[],
        );
      } catch (err) {
        console.warn('[voice-rx] inventory candidate load failed:', err);
        candidateRows = [];
      }
      const candById = new Map(candidateRows.map((r) => [r.id, r]));
      const matchCandidates: InventoryMatchCandidate[] = candidateRows.map((r) => ({
        inventoryItemId: r.id,
        displayName: r.item_name ?? '',
      }));

      const resolved = resolveVoiceMedications(medications, matchCandidates);
      const now = new Date().toISOString();
      const base = sortBaseRef.current;
      let added = 0;

      for (let i = 0; i < resolved.length; i++) {
        const { extracted, matchStatus, inventoryItemId } = resolved[i];
        const fields = extractedMedicationToDraftFields(extracted);

        let itemName = extracted.medicineName;
        let itemDetails: string | null = null;
        let invId: string | null = null;
        let snap = emptyLineSnapshot();

        if (matchStatus === 'matched' && inventoryItemId) {
          const row = candById.get(inventoryItemId);
          if (row) {
            const selection = pickerCandidateRowToSelection(row);
            const s = toPrescriptionLineSnapshot(selection);
            itemName = selection.itemName;
            itemDetails = buildItemDetailsFromSelection(selection) || null;
            invId = selection.inventoryItemId;
            snap = s;
          }
        }
        // Unmatched / ambiguous stay unresolved: free-text name, no inventory
        // link, `Match inventory` surfaced in the grid.
        const freeTextName = invId ? null : extracted.medicineName;
        const durationValue = fields.durationValue.trim()
          ? parseInt(fields.durationValue, 10)
          : null;

        try {
          await db.execute(
            `INSERT INTO patient_prescriptions
              (id, org_id, clinic_id, patient_id, encounter_id, visit_id,
               item_name, item_details,
               inventory_item_id, free_text_name,
               item_name_snapshot, sku_snapshot, generic_name_snapshot, brand_name_snapshot,
               strength_snapshot, dosage_form_snapshot, route_snapshot, unit_snapshot,
               default_instruction_snapshot, default_dosage_snapshot, usage_snapshot,
               dose_snapshot, dose_unit_snapshot, frequency_snapshot, duration_snapshot,
               intake_timing, dose_quantity,
               schedule_morning, schedule_afternoon, schedule_night,
               duration_value, duration_unit, instructions, sort_order,
               source, status,
               author_user_id, author_name, created_at, updated_at)
             VALUES (?, ?, ?, ?, ?, ?,
                     ?, ?,
                     ?, ?,
                     ?, ?, ?, ?,
                     ?, ?, ?, ?,
                     ?, ?, ?,
                     ?, ?, ?, ?,
                     ?, ?,
                     ?, ?, ?,
                     ?, ?, ?, ?,
                     ?, ?,
                     ?, ?, ?, ?)`,
            [
              crypto.randomUUID(),
              orgId,
              null,
              patientId,
              encounterId,
              visitId ?? null,
              itemName,
              itemDetails,
              invId,
              freeTextName,
              invId ? snap.item_name_snapshot : null,
              snap.sku_snapshot,
              snap.generic_name_snapshot,
              snap.brand_name_snapshot,
              snap.strength_snapshot,
              snap.dosage_form_snapshot,
              snap.route_snapshot,
              snap.unit_snapshot,
              snap.default_instruction_snapshot,
              snap.default_dosage_snapshot,
              snap.usage_snapshot,
              snap.dose_snapshot,
              snap.dose_unit_snapshot,
              snap.frequency_snapshot,
              snap.duration_snapshot,
              fields.intakeTiming || null,
              fields.doseQuantity.trim() || null,
              fields.scheduleMorning ? 1 : 0,
              fields.scheduleAfternoon ? 1 : 0,
              fields.scheduleNight ? 1 : 0,
              durationValue != null && !Number.isNaN(durationValue) ? durationValue : null,
              fields.durationUnit || 'days',
              fields.instructions.trim() || null,
              base + i,
              'ai',
              // PRD.280 (as-built revision) — auto-accept: AI voice rows land as
              // clinician-confirmed 'final' (matching the dermatology AI
              // auto-apply precedent), so no manual Accept step is needed. The
              // 'ai' source keeps the "AI Suggested" badge + allergy banner.
              'final',
              userId || null,
              userName || null,
              now,
              now,
            ],
          );
          added += 1;
        } catch (err) {
          console.warn('[voice-rx] prescription insert failed:', err);
        }
      }

      if (added > 0) {
        setAppliedToast(`${added} prescription item${added === 1 ? '' : 's'} added`);
      }
      return added;
    },
    [db, orgId, patientId, encounterId, visitId, userId, userName],
  );

  const voice = useVoicePrescription({
    context: { orgId, patientId, visitId, encounterId },
    permitted: canVoicePrescribe,
    applyMedications: applyVoiceMedications,
  });

  // ─── PRD.219 — Prescription template suggestions + apply ───

  const [templateSearch, setTemplateSearch] = useState('');
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const [appliedToast, setAppliedToast] = useState<string | null>(null);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  // PRD.238 — WhatsApp share modal state.
  const [shareOpen, setShareOpen] = useState(false);
  // Only active templates are surfaced in the encounter flow.
  const { data: templates = [] } = usePowerSyncQuery<PrescriptionTemplateListRow>(
    qPrescriptionTemplates({ orgId, status: 'active', search: templateSearch }),
  );

  // Resolve currently-applied source template ids across saved + pending
  // drafts so the duplicate-confirmation can trigger reliably.
  const appliedSourceTemplateIds = useMemo(() => {
    const ids = new Set<string>();
    for (const d of allDrafts) {
      if (d.sourceTemplateId) ids.add(d.sourceTemplateId);
    }
    return ids;
  }, [allDrafts]);

  // Apply a template — append item rows as pending drafts, never erase
  // existing ones (PRD.219 §6.4). Triggers a duplicate confirmation when the
  // same template was already applied in the current draft.
  const handleApplyTemplate = useCallback(
    async (templateId: string, templateName: string) => {
      if (!db) return;
      if (appliedSourceTemplateIds.has(templateId)) {
        const ok = window.confirm(
          'Apply this template again?\n' +
            'This template has already been added to the current prescription draft. ' +
            'Applying it again may create duplicate medicines.',
        );
        if (!ok) return;
      }

      // PowerSync queries via the hook are reactive, but the apply action is
      // imperative — fetch the items once via the same db handle so the
      // append happens in a single batch.
      let items: PrescriptionTemplateItemRow[] = [];
      try {
        const result = await db.getAll<PrescriptionTemplateItemRow>(
          `SELECT * FROM prescription_template_items WHERE template_id = ? ORDER BY sort_order ASC, created_at ASC`,
          [templateId],
        );
        items = result ?? [];
      } catch (err) {
        console.error('[EncounterPrescriptions] apply template — load items failed:', err);
        window.alert('Could not apply template.\nYour current prescription draft was not changed.');
        return;
      }

      if (items.length === 0) {
        window.alert('This template has no medicines to apply.');
        return;
      }

      const baseSortOrder = allDrafts.length;
      const newDrafts: PrescriptionDraft[] = items.map((it, i) => ({
        id: crypto.randomUUID(),
        isNew: true,
        itemName: it.item_name,
        itemDetails: it.item_details ?? '',
        intakeTiming: it.intake_timing ?? '',
        doseQuantity: it.dose_quantity ?? '',
        scheduleMorning: it.schedule_morning === 1,
        scheduleAfternoon: it.schedule_afternoon === 1,
        scheduleNight: it.schedule_night === 1,
        durationValue: it.duration_value != null ? String(it.duration_value) : '',
        durationUnit: it.duration_unit ?? 'days',
        instructions: it.instructions ?? '',
        sortOrder: baseSortOrder + i,
        // PRD.219 §8 — direct field copy.
        inventoryItemId: it.inventory_item_id,
        // If no inventory link, mirror item_name into free_text_name (PRD.219 §8).
        freeTextName: it.inventory_item_id ? null : it.item_name,
        itemNameSnapshot: it.item_name_snapshot,
        skuSnapshot: it.sku_snapshot,
        genericNameSnapshot: it.generic_name_snapshot,
        brandNameSnapshot: it.brand_name_snapshot,
        strengthSnapshot: it.strength_snapshot,
        dosageFormSnapshot: it.dosage_form_snapshot,
        routeSnapshot: it.route_snapshot,
        unitSnapshot: it.unit_snapshot,
        // patient_prescriptions has extra snapshot columns (default_*,
        // dose_*, usage_*, frequency_*, duration_snapshot) that don't have
        // a template equivalent. Leave them null on applied rows.
        defaultInstructionSnapshot: null,
        defaultDosageSnapshot: null,
        usageSnapshot: null,
        doseSnapshot: null,
        doseUnitSnapshot: null,
        frequencySnapshot: null,
        durationSnapshot: null,
        sourceTemplateId: templateId,
        // PRD.217 — template-applied rows are clinician-confirmed prescriptions,
        // not AI drafts. They behave exactly like manually-added rows.
        source: 'template',
        status: 'final',
      }));
      setPendingDrafts((prev) => [...prev, ...newDrafts]);
      setPreviewTemplateId(null);
      setAppliedToast(`Applied "${templateName}". Review medicines before saving.`);
    },
    [db, allDrafts.length, appliedSourceTemplateIds],
  );

  // Auto-dismiss the apply toast after a few seconds.
  useEffect(() => {
    if (!appliedToast) return;
    const t = window.setTimeout(() => setAppliedToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [appliedToast]);

  // ─── Save current draft as a prescription template ───
  // Saveable rows = any draft (saved or pending) that has a non-empty item
  // name. Field mapping is the inverse of PRD.219 apply mapping.

  const saveableDrafts = useMemo(
    () => allDrafts.filter((d) => d.itemName.trim().length > 0),
    [allDrafts],
  );
  const canSaveTemplate = saveableDrafts.length > 0;

  const handleSaveAsTemplate = useCallback(
    async (input: SaveAsTemplateInput) => {
      if (!db) return;
      if (saveableDrafts.length === 0) return;
      setSavingTemplate(true);
      try {
        const now = new Date().toISOString();
        const templateId = crypto.randomUUID();
        await db.execute(
          `INSERT INTO prescription_templates
            (id, org_id, name, description, category, specialty,
             diagnosis_code, diagnosis_label, default_instructions,
             is_active, created_by, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            templateId,
            orgId,
            input.name.trim(),
            input.description.trim() || null,
            input.category.trim() || null,
            input.specialty.trim() || null,
            // Diagnosis columns retained in schema but no longer authored in
            // the builder UI.
            null,
            null,
            input.defaultInstructions.trim() || null,
            input.isActive ? 1 : 0,
            userId,
            now,
            now,
          ],
        );
        for (let i = 0; i < saveableDrafts.length; i++) {
          const d = saveableDrafts[i];
          const durTrimmed = d.durationValue.trim();
          const durNum = durTrimmed === '' ? null : parseInt(durTrimmed, 10);
          await db.execute(
            `INSERT INTO prescription_template_items
              (id, org_id, template_id, inventory_item_id,
               item_name, item_details,
               item_name_snapshot, sku_snapshot, generic_name_snapshot, brand_name_snapshot,
               strength_snapshot, dosage_form_snapshot, route_snapshot, unit_snapshot,
               intake_timing, dose_quantity,
               schedule_morning, schedule_afternoon, schedule_night,
               duration_value, duration_unit, instructions, sort_order,
               created_at, updated_at)
             VALUES (?, ?, ?, ?,
                     ?, ?,
                     ?, ?, ?, ?,
                     ?, ?, ?, ?,
                     ?, ?,
                     ?, ?, ?,
                     ?, ?, ?, ?,
                     ?, ?)`,
            [
              crypto.randomUUID(),
              orgId,
              templateId,
              d.inventoryItemId,
              d.itemName.trim(),
              d.itemDetails.trim() || null,
              d.itemNameSnapshot,
              d.skuSnapshot,
              d.genericNameSnapshot,
              d.brandNameSnapshot,
              d.strengthSnapshot,
              d.dosageFormSnapshot,
              d.routeSnapshot,
              d.unitSnapshot,
              d.intakeTiming || null,
              d.doseQuantity.trim() || null,
              d.scheduleMorning ? 1 : 0,
              d.scheduleAfternoon ? 1 : 0,
              d.scheduleNight ? 1 : 0,
              durNum != null && !Number.isNaN(durNum) ? durNum : null,
              d.durationUnit || null,
              d.instructions.trim() || null,
              i,
              now,
              now,
            ],
          );
        }
        setSaveAsTemplateOpen(false);
        setAppliedToast(`Saved as template "${input.name.trim()}".`);
      } catch (err) {
        console.error('[EncounterPrescriptions] save as template failed:', err);
        window.alert('Could not save template. Please try again.');
      } finally {
        setSavingTemplate(false);
      }
    },
    [db, orgId, userId, saveableDrafts],
  );

  const handleSave = useCallback(
    async (draft: PrescriptionDraft) => {
      if (!db) return;
      if (!draft.itemName.trim()) return;
      setSavingId(draft.id);
      try {
        const now = new Date().toISOString();
        const durationValue = draft.durationValue.trim() ? parseInt(draft.durationValue, 10) : null;

        // PRD.189 — derive free_text_name when no inventory item is bound.
        // Always also write the legacy item_name (NOT NULL) for backward
        // compatibility and to keep existing display paths working.
        const freeTextName = draft.inventoryItemId ? null : draft.itemName.trim() || null;

        if (draft.isNew) {
          await db.execute(
            `INSERT INTO patient_prescriptions
              (id, org_id, clinic_id, patient_id, encounter_id, visit_id,
               item_name, item_details,
               inventory_item_id, free_text_name,
               item_name_snapshot, sku_snapshot, generic_name_snapshot, brand_name_snapshot,
               strength_snapshot, dosage_form_snapshot, route_snapshot, unit_snapshot,
               default_instruction_snapshot, default_dosage_snapshot, usage_snapshot,
               dose_snapshot, dose_unit_snapshot, frequency_snapshot, duration_snapshot,
               intake_timing, dose_quantity,
               schedule_morning, schedule_afternoon, schedule_night,
               duration_value, duration_unit, instructions, sort_order,
               source_template_id,
               source, status,
               author_user_id, author_name, created_at, updated_at)
             VALUES (?, ?, ?, ?, ?, ?,
                     ?, ?,
                     ?, ?,
                     ?, ?, ?, ?,
                     ?, ?, ?, ?,
                     ?, ?, ?,
                     ?, ?, ?, ?,
                     ?, ?,
                     ?, ?, ?,
                     ?, ?, ?, ?,
                     ?,
                     ?, ?,
                     ?, ?, ?, ?)`,
            [
              draft.id,
              orgId,
              null,
              patientId,
              encounterId,
              visitId ?? null,
              draft.itemName.trim(),
              draft.itemDetails.trim() || null,
              draft.inventoryItemId,
              freeTextName,
              draft.itemNameSnapshot,
              draft.skuSnapshot,
              draft.genericNameSnapshot,
              draft.brandNameSnapshot,
              draft.strengthSnapshot,
              draft.dosageFormSnapshot,
              draft.routeSnapshot,
              draft.unitSnapshot,
              draft.defaultInstructionSnapshot,
              draft.defaultDosageSnapshot,
              draft.usageSnapshot,
              draft.doseSnapshot,
              draft.doseUnitSnapshot,
              draft.frequencySnapshot,
              draft.durationSnapshot,
              draft.intakeTiming || null,
              draft.doseQuantity.trim() || null,
              draft.scheduleMorning ? 1 : 0,
              draft.scheduleAfternoon ? 1 : 0,
              draft.scheduleNight ? 1 : 0,
              durationValue != null && !Number.isNaN(durationValue) ? durationValue : null,
              draft.durationUnit || null,
              draft.instructions.trim() || null,
              draft.sortOrder,
              draft.sourceTemplateId,
              // PRD.280 — persist provenance so AI/template rows keep their
              // source + draft/final lifecycle after edit (manual rows: null +
              // 'final', matching the prior DB defaults exactly).
              draft.source,
              draft.status || 'final',
              userId,
              userName,
              now,
              now,
            ],
          );
          setPendingDrafts((prev) => prev.filter((d) => d.id !== draft.id));
        } else {
          await db.execute(
            `UPDATE patient_prescriptions
             SET item_name = ?, item_details = ?,
                 inventory_item_id = ?, free_text_name = ?,
                 item_name_snapshot = ?, sku_snapshot = ?, generic_name_snapshot = ?,
                 brand_name_snapshot = ?, strength_snapshot = ?, dosage_form_snapshot = ?,
                 route_snapshot = ?, unit_snapshot = ?,
                 default_instruction_snapshot = ?, default_dosage_snapshot = ?, usage_snapshot = ?,
                 dose_snapshot = ?, dose_unit_snapshot = ?, frequency_snapshot = ?, duration_snapshot = ?,
                 intake_timing = ?, dose_quantity = ?,
                 schedule_morning = ?, schedule_afternoon = ?, schedule_night = ?,
                 duration_value = ?, duration_unit = ?, instructions = ?, sort_order = ?,
                 source_template_id = ?,
                 updated_at = ?
             WHERE id = ?`,
            [
              draft.itemName.trim(),
              draft.itemDetails.trim() || null,
              draft.inventoryItemId,
              freeTextName,
              draft.itemNameSnapshot,
              draft.skuSnapshot,
              draft.genericNameSnapshot,
              draft.brandNameSnapshot,
              draft.strengthSnapshot,
              draft.dosageFormSnapshot,
              draft.routeSnapshot,
              draft.unitSnapshot,
              draft.defaultInstructionSnapshot,
              draft.defaultDosageSnapshot,
              draft.usageSnapshot,
              draft.doseSnapshot,
              draft.doseUnitSnapshot,
              draft.frequencySnapshot,
              draft.durationSnapshot,
              draft.intakeTiming || null,
              draft.doseQuantity.trim() || null,
              draft.scheduleMorning ? 1 : 0,
              draft.scheduleAfternoon ? 1 : 0,
              draft.scheduleNight ? 1 : 0,
              durationValue != null && !Number.isNaN(durationValue) ? durationValue : null,
              draft.durationUnit || null,
              draft.instructions.trim() || null,
              draft.sortOrder,
              draft.sourceTemplateId,
              now,
              draft.id,
            ],
          );
          setEditingDrafts((prev) => {
            const next = { ...prev };
            delete next[draft.id];
            return next;
          });
        }
      } finally {
        setSavingId(null);
      }
    },
    [db, orgId, patientId, encounterId, visitId, userId, userName],
  );

  const handleRemove = useCallback(
    async (draft: PrescriptionDraft) => {
      if (draft.isNew) {
        setPendingDrafts((prev) => prev.filter((d) => d.id !== draft.id));
        return;
      }
      if (!db) return;
      await db.execute(`DELETE FROM patient_prescriptions WHERE id = ?`, [draft.id]);
      setEditingDrafts((prev) => {
        const next = { ...prev };
        delete next[draft.id];
        return next;
      });
    },
    [db],
  );

  // PRD.217 §12 — promote an AI draft prescription to final after clinician
  // review. `source='ai'` is preserved for historical provenance per the
  // user's spec; reviewed_by / reviewed_at form an audit trail.
  const handleAccept = useCallback(
    async (draft: PrescriptionDraft) => {
      if (!db || draft.isNew || draft.status !== 'draft') return;
      const now = new Date().toISOString();
      await db.execute(
        `UPDATE patient_prescriptions
            SET status = 'final',
                reviewed_by = ?,
                reviewed_at = ?,
                updated_at = ?
          WHERE id = ?`,
        [userId || null, now, now, draft.id],
      );
      // Drop any in-flight edits so the row re-syncs from the persisted
      // state with status='final' on the next watcher tick.
      setEditingDrafts((prev) => {
        const next = { ...prev };
        delete next[draft.id];
        return next;
      });
    },
    [db, userId],
  );

  // ─── Allergy override (soft-stop) ───
  // A line whose medicine matches a documented allergy must be acknowledged
  // with a reason before it saves. Acknowledgements are keyed by line + the
  // specific conflict set, so changing the drug to a different allergen
  // re-prompts. Acknowledged keys live for the session; the recorded note is
  // the durable audit trail.
  const acknowledgedRef = useRef<Set<string>>(new Set());
  const [allergyPrompt, setAllergyPrompt] = useState<{
    draft: PrescriptionDraft;
    conflicts: AllergyConflict[];
    action: 'save' | 'accept';
  } | null>(null);

  // Record an override as a warning/critical patient note (durable audit trail
  // that also surfaces in the patient timeline + alerts).
  const recordAllergyOverride = useCallback(
    async (draft: PrescriptionDraft, conflicts: AllergyConflict[], reason: string) => {
      if (!db) return;
      const now = new Date().toISOString();
      const drug = draft.itemName.trim() || draft.freeTextName?.trim() || 'a medication';
      const list = conflicts
        .map((c) => (c.severity !== 'unknown' ? `${c.substance} (${c.severity})` : c.substance))
        .join(', ');
      const body =
        `Allergy override — prescribed "${drug}" despite a documented allergy to ${list}. ` +
        `Reason: ${reason.trim()}`;
      const alertLevel = highestSeverity(conflicts) === 'severe' ? 'critical' : 'warning';
      await db.execute(
        `INSERT INTO patient_notes
          (id, org_id, clinic_id, patient_id, encounter_id, visit_id, title, body, note_type, visibility, alert_level, author_user_id, author_name, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          orgId,
          null,
          patientId,
          encounterId,
          visitId ?? null,
          'Allergy override',
          body,
          'clinical',
          'team',
          alertLevel,
          userId || null,
          userName || null,
          now,
          now,
        ],
      );
    },
    [db, orgId, patientId, encounterId, visitId, userId, userName],
  );

  // Guarded entry points used by the grid: intercept a save/accept when the
  // line conflicts with an allergy and hasn't been acknowledged yet.
  const requestSave = useCallback(
    (draft: PrescriptionDraft) => {
      const conflicts = findAllergyConflicts(draft, allergyRows);
      if (
        conflicts.length > 0 &&
        !acknowledgedRef.current.has(conflictAckKey(draft.id, conflicts))
      ) {
        setAllergyPrompt({ draft, conflicts, action: 'save' });
        return;
      }
      handleSave(draft);
    },
    [allergyRows, handleSave],
  );

  const requestAccept = useCallback(
    (draft: PrescriptionDraft) => {
      const conflicts = findAllergyConflicts(draft, allergyRows);
      if (
        conflicts.length > 0 &&
        !acknowledgedRef.current.has(conflictAckKey(draft.id, conflicts))
      ) {
        setAllergyPrompt({ draft, conflicts, action: 'accept' });
        return;
      }
      handleAccept(draft);
    },
    [allergyRows, handleAccept],
  );

  const confirmAllergyOverride = useCallback(
    async (reason: string) => {
      if (!allergyPrompt) return;
      const { draft, conflicts, action } = allergyPrompt;
      acknowledgedRef.current.add(conflictAckKey(draft.id, conflicts));
      setAllergyPrompt(null);
      await recordAllergyOverride(draft, conflicts, reason);
      if (action === 'accept') handleAccept(draft);
      else handleSave(draft);
    },
    [allergyPrompt, recordAllergyOverride, handleAccept, handleSave],
  );

  const printDisabled = printItems.length === 0;

  // ── PRD.305 — ONE Prescription canvas, two ordering groups ──
  //
  // The canvas represents everything the clinician ordered in this
  // consultation, so medications and laboratory requests are visible together
  // as groups rather than behind tabs. Medications keeps the existing UI and
  // behaviour untouched; laboratory writes canonical lab_orders /
  // lab_order_tests and never patient_prescriptions. Templates and voice stay
  // medication-specific.
  const [addLabTestOpen, setAddLabTestOpen] = useState(false);
  const labCapabilities = useLaboratoryCapabilities('consultation');
  const labAvailable = isLaboratoryViewAvailable({
    permissionsReady: labCapabilities.ready,
    capabilities: labCapabilities,
  });
  // Hiding, not disabling: an action the clinic's Laboratory Settings or the
  // user's role rules out is absent rather than greyed out, matching how every
  // other laboratory surface (the module, LabsSurface) already behaves. A
  // permanently dead button just invites clicking.
  const canAddLabTest = labAvailable && labCapabilities.createOrder;

  const { data: labOrderRows = [] } = usePowerSyncQuery<LabOrderRow>(
    qLabOrdersForEncounter(encounterId),
  );
  // Filtered with the SAME selector the group uses, so the canvas's empty state
  // can never sit above a populated laboratory list.
  const visibleLabOrders = useMemo(
    () => selectVisibleLabOrders(labOrderRows, labCapabilities.viewOperational),
    [labOrderRows, labCapabilities.viewOperational],
  );

  // Group labels only earn their space once the canvas actually holds more than
  // one kind of thing — a clinic with no laboratory sees the pre-PRD.305 layout.
  const showGroupHeadings = labAvailable || visibleLabOrders.length > 0;
  const canvasEmpty = allDrafts.length === 0 && visibleLabOrders.length === 0;

  // Download the prescription as a PNG — fully client-side. Reuses the same
  // offscreen PrescriptionPrintLayout the WhatsApp share renders, captures it
  // with html2canvas, and triggers a browser download (no print dialog).
  const handleDownload = useCallback(async () => {
    if (printDisabled || downloading || !downloadRef.current) return;
    setDownloading(true);
    try {
      const png = await renderNodeToPng({ node: downloadRef.current });
      if (!png.dataUrl) return;
      const safeName = (printPatient.name || 'patient').replace(/[^\w.-]+/g, '_');
      const date = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = png.dataUrl;
      a.download = `Prescription-${safeName}-${date}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error('[prescription-download] image render failed', err);
    } finally {
      setDownloading(false);
    }
  }, [printDisabled, downloading, printPatient.name]);

  return (
    <>
      <div
        style={{
          padding: 'var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {!hideHeader && (
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                Prescription
              </h2>
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                }}
              >
                Items prescribed and ordered during this consultation
              </p>
            </div>
          )}
          {hideHeader && <div />}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            {/* Download Prescription — captures the same print layout to a PNG
                client-side (html2canvas) and downloads it, no print dialog. */}
            <button
              type="button"
              onClick={handleDownload}
              disabled={printDisabled || downloading}
              title={
                printDisabled
                  ? 'Add at least one item before downloading'
                  : 'Download this prescription as an image'
              }
              style={{
                height: quietActions ? 30 : 36,
                padding: quietActions ? '0 10px' : '0 14px',
                borderRadius: quietActions ? 'var(--radius-sm)' : 'var(--radius-md)',
                border: `1px solid ${printDisabled || downloading ? 'var(--border)' : primary}`,
                background: 'var(--bg-surface)',
                color: printDisabled || downloading ? 'var(--text-disabled)' : primary,
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: printDisabled || downloading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <NavIcon name="download" />
              {!quietActions && (downloading ? 'Preparing…' : 'Download')}
            </button>
            {/* Print Prescription — uses the saved org-default print layout. */}
            <button
              type="button"
              onClick={handlePrint}
              disabled={printDisabled || printing}
              title={
                printDisabled
                  ? 'Add at least one item before printing'
                  : 'Print using the saved prescription layout'
              }
              style={{
                height: quietActions ? 30 : 36,
                padding: quietActions ? '0 10px' : '0 14px',
                borderRadius: quietActions ? 'var(--radius-sm)' : 'var(--radius-md)',
                border: `1px solid ${printDisabled || printing ? 'var(--border)' : primary}`,
                background: 'var(--bg-surface)',
                color: printDisabled || printing ? 'var(--text-disabled)' : primary,
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: printDisabled || printing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <NavIcon name="file-text" />
              {!quietActions && (printing ? 'Preparing…' : 'Print')}
            </button>
            {/* PRD.238 — Share via WhatsApp (image of the same print layout). */}
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              disabled={printDisabled}
              title={
                printDisabled
                  ? 'Add at least one item before sharing'
                  : 'Share this prescription as a WhatsApp image'
              }
              style={{
                height: quietActions ? 30 : 36,
                padding: quietActions ? '0 10px' : '0 14px',
                borderRadius: quietActions ? 'var(--radius-sm)' : 'var(--radius-md)',
                border: `1px solid ${printDisabled ? 'var(--border)' : primary}`,
                background: 'var(--bg-surface)',
                color: printDisabled ? 'var(--text-disabled)' : primary,
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: printDisabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <NavIcon name="message-square" />
              {!quietActions && 'Share'}
            </button>
            {/* PRD.305 — two direct actions rather than one menu: adding a
                medication or a lab test is a single click each. The medication
                button is unchanged; the laboratory one only appears where
                laboratory ordering is available.

                In the compact (quietActions) row the laboratory action keeps a
                short "Lab" label instead of going icon-only: a second bare
                glyph next to "+" gives no clue which one orders what, and
                `clipboard` is already the prescription icon in this app. */}
            {canAddLabTest && (
              <span
                aria-hidden
                style={{
                  width: 1,
                  alignSelf: 'stretch',
                  margin: '0 var(--sp-1)',
                  background: 'var(--border)',
                }}
              />
            )}
            {quietActions ? (
              <button
                onClick={handleAddRow}
                title="Add Medication"
                aria-label="Add Medication"
                style={{
                  // Icon-only when there is no laboratory action to be confused
                  // with — that is the exact pre-PRD.305 button.
                  width: canAddLabTest ? undefined : 30,
                  height: 30,
                  padding: canAddLabTest ? '0 8px' : 0,
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-semi)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                }}
              >
                <NavIcon name="plus" />
                {canAddLabTest && 'Medicine'}
              </button>
            ) : (
              <button
                onClick={handleAddRow}
                style={{
                  height: 36,
                  padding: '0 16px',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  background: primary,
                  color: '#fff',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <NavIcon name="plus" /> Add Medication
              </button>
            )}

            {canAddLabTest &&
              (quietActions ? (
                <button
                  onClick={() => setAddLabTestOpen(true)}
                  title="Add a laboratory test to this consultation"
                  aria-label="Add Lab Test"
                  style={{
                    height: 30,
                    padding: '0 8px',
                    borderRadius: 'var(--radius-sm)',
                    border: `1px solid ${primary}`,
                    background: 'var(--bg-surface)',
                    color: primary,
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semi)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <NavIcon name="plus" /> Lab
                </button>
              ) : (
                <button
                  onClick={() => setAddLabTestOpen(true)}
                  title="Add a laboratory test to this consultation"
                  style={{
                    height: 36,
                    padding: '0 14px',
                    borderRadius: 'var(--radius-md)',
                    border: `1px solid ${primary}`,
                    background: 'var(--bg-surface)',
                    color: primary,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <NavIcon name="plus" /> Add Lab Test
                </button>
              ))}
            {/* PRD.280 — separate microphone action for voice prescription.
                Idle uses the normal brand color; red is reserved for active
                recording. Hidden when the feature/permission gate fails. */}
            {voice.available &&
              (quietActions ? (
                <button
                  onClick={voice.start}
                  disabled={!voice.canStart}
                  title="Add prescription using voice"
                  aria-label="Add prescription using voice"
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 'var(--radius-sm)',
                    border: `1px solid ${primary}`,
                    background: 'var(--bg-surface)',
                    color: primary,
                    cursor: voice.canStart ? 'pointer' : 'not-allowed',
                    opacity: voice.canStart ? 1 : 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                  }}
                >
                  <NavIcon name="mic" />
                </button>
              ) : (
                <button
                  onClick={voice.start}
                  disabled={!voice.canStart}
                  title="Add prescription using voice"
                  aria-label="Add prescription using voice"
                  style={{
                    height: 36,
                    padding: '0 14px',
                    borderRadius: 'var(--radius-md)',
                    border: `1px solid ${primary}`,
                    background: 'var(--bg-surface)',
                    color: primary,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    cursor: voice.canStart ? 'pointer' : 'not-allowed',
                    opacity: voice.canStart ? 1 : 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <NavIcon name="mic" /> Add by voice
                </button>
              ))}
          </div>
        </div>

        {/* PRD.280 — voice recording / processing / failure panel. */}
        {voice.available && <VoicePrescriptionPanel voice={voice} primary={primary} />}

        {/* PRD.219 — Prescription Templates suggestions */}
        <SuggestedTemplatesPanel
          templates={templates}
          search={templateSearch}
          onSearchChange={setTemplateSearch}
          appliedIds={appliedSourceTemplateIds}
          onPreview={(id) => setPreviewTemplateId(id)}
          onApply={(id, name) => handleApplyTemplate(id, name)}
          canSaveAsTemplate={canSaveTemplate}
          onSaveAsTemplate={() => setSaveAsTemplateOpen(true)}
        />

        {appliedToast && (
          <div
            role="status"
            style={{
              padding: '8px 12px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-success-text, #166534)',
              background: 'var(--color-success-bg, #dcfce7)',
              color: 'var(--color-success-text, #166534)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <NavIcon name="check" />
            {appliedToast}
          </div>
        )}

        {/* Allergy conflict summary — a soft-stop warning surfaced above the
            grid when any prescribed line matches a documented allergy. */}
        {conflictSummary.lineCount > 0 && (
          <AllergyConflictBanner
            lineCount={conflictSummary.lineCount}
            items={conflictSummary.items}
          />
        )}

        {/* Nothing ordered at all — one empty state for the whole canvas, not
            one per group. */}
        {canvasEmpty && (
          <div
            style={{
              textAlign: 'center',
              padding: 'var(--sp-8)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              border: '2px dashed var(--border)',
              borderRadius: 'var(--radius-lg)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--sp-3)',
            }}
          >
            <span>
              {labAvailable ? 'Nothing prescribed or ordered yet.' : 'No prescription items yet.'}
            </span>
            {/* Same one-click-each pair as the header. */}
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <button onClick={handleAddRow} style={emptyStateCtaStyle(primary)}>
                <NavIcon name="plus" /> {canAddLabTest ? 'Add Medication' : 'Add Item'}
              </button>
              {canAddLabTest && (
                <button onClick={() => setAddLabTestOpen(true)} style={emptyStateCtaStyle(primary)}>
                  <NavIcon name="plus" /> Add Lab Test
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Medications group — existing grid, behaviour unchanged ── */}
        {allDrafts.length > 0 && (
          <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            {showGroupHeadings && (
              <h3
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  color: 'var(--text-muted)',
                }}
              >
                Medications
              </h3>
            )}
            <PrescriptionGrid
              drafts={allDrafts}
              dirtyIds={new Set(allDrafts.filter((d) => isRowDirty(d)).map((d) => d.id))}
              savingId={savingId}
              primary={primary}
              conflictsByDraftId={conflictsByDraftId}
              onChange={updateDraft}
              onSave={requestSave}
              onRemove={handleRemove}
              onAccept={requestAccept}
            />
          </section>
        )}

        {/* ── Laboratory Requests group — canonical lab_orders/lab_order_tests.
            Renders its heading + list only when it holds items; its drawers
            mount regardless so the first test can be added from Add Item. ── */}
        <EncounterLabOrdersGroup
          patientId={patientId}
          encounterId={encounterId}
          visitId={visitId ?? null}
          providerId={encounterRows[0]?.provider_id ?? null}
          orders={visibleLabOrders}
          capabilities={labCapabilities}
          showHeading={showGroupHeadings}
          addOpen={addLabTestOpen}
          onAddOpenChange={setAddLabTestOpen}
        />
      </div>

      {/* Same feedback the Visit Note print actions show, so the two surfaces
          signal the same operation identically. */}
      {(printing || printError) && (
        <PrintProgressToast
          message="Preparing Prescription…"
          error={printError}
          onDismiss={() => setPrintError(null)}
        />
      )}

      {/* Offscreen capture mount for the Download action. Unlike the
          print-only node (display:none off-print, so html2canvas can't
          rasterize it), this stays laid out at A4 width just off-screen. */}
      {!printDisabled && (
        <div
          ref={downloadRef}
          aria-hidden
          style={{
            position: 'fixed',
            left: '-99999px',
            top: 0,
            width: '210mm',
            background: '#ffffff',
            pointerEvents: 'none',
          }}
        >
          <PrescriptionPrintLayout
            settings={printSettings}
            doctor={printDoctor}
            patient={printPatient}
            date={new Date().toISOString()}
            items={printItems}
            complaint={printComplaint}
            vitalsSummary={printVitalsSummary}
            clinical={printClinical}
            vitals={printVitals}
            accentColor={primary}
            fontColor={printBranding.fontColor}
            letterhead={printBranding.letterhead}
          />
        </div>
      )}

      {/* PRD.284 — the "Print" button renders the prescription into an isolated
          iframe (printHtmlViaIframe) instead of a hidden on-page mount, so the
          host app page can never leak into the printout on tablet/mobile. */}

      {/* PRD.238 — Share Prescription via WhatsApp */}
      <SharePrescriptionModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        patientId={patientId}
        patientName={printPatient.name}
        patientWhatsAppNumber={
          contactRows[0]?.mobile_number
            ? (contactRows[0].mobile_country_code
                ? `+${contactRows[0].mobile_country_code.replace(/^\+/, '')} `
                : '') + contactRows[0].mobile_number
            : null
        }
        encounterId={encounterId}
        prescriptionIds={rows.map((r) => r.id)}
        printSettings={printSettings}
        printDoctor={printDoctor}
        printPatient={printPatient}
        printItems={printItems}
        printComplaint={printComplaint}
        printClinical={printClinical}
        printVitals={printVitals}
        printVitalsSummary={printVitalsSummary}
        accentColor={primary}
        fontColor={printBranding.fontColor}
        letterhead={printBranding.letterhead}
      />

      {/* PRD.219 — Template preview drawer */}
      <TemplatePreviewDrawer
        templateId={previewTemplateId}
        onClose={() => setPreviewTemplateId(null)}
        onApply={(id, name) => handleApplyTemplate(id, name)}
        primary={primary}
      />

      {/* Save current prescription as a new template (PRD.218 builder reuse). */}
      {saveAsTemplateOpen && (
        <SaveAsTemplateModal
          itemCount={saveableDrafts.length}
          saving={savingTemplate}
          onCancel={() => setSaveAsTemplateOpen(false)}
          onSave={handleSaveAsTemplate}
          primary={primary}
        />
      )}

      {/* Allergy override confirmation — requires a recorded reason before a
          conflicting line is saved/accepted. */}
      {allergyPrompt && (
        <AllergyOverrideModal
          medicineName={allergyPrompt.draft.itemName.trim() || 'this medication'}
          conflicts={allergyPrompt.conflicts}
          onCancel={() => setAllergyPrompt(null)}
          onConfirm={confirmAllergyOverride}
        />
      )}
    </>
  );
}

// ─── PRD.219 — Suggested Templates panel ───

function SuggestedTemplatesPanel({
  templates,
  search,
  onSearchChange,
  appliedIds,
  onPreview,
  onApply,
  canSaveAsTemplate,
  onSaveAsTemplate,
}: {
  templates: PrescriptionTemplateListRow[];
  search: string;
  onSearchChange: (v: string) => void;
  appliedIds: Set<string>;
  onPreview: (id: string) => void;
  onApply: (id: string, name: string) => void;
  canSaveAsTemplate: boolean;
  onSaveAsTemplate: () => void;
}) {
  // No card chrome — render pills inline directly. When there are too many
  // templates to fit in ~2 rows we cap the inline count and surface a "…"
  // overflow pill that opens a small search popover for the rest.
  const MAX_INLINE = 6;
  const isSearching = search.trim().length > 0;
  const [overflowOpen, setOverflowOpen] = useState(false);
  const inlineTemplates = isSearching
    ? templates.slice(0, MAX_INLINE)
    : templates.slice(0, MAX_INLINE);
  const hasOverflow = templates.length > MAX_INLINE;

  // Hide the row entirely when there's nothing to show, no search active,
  // and nothing to save as a new template.
  if (templates.length === 0 && !isSearching && !canSaveAsTemplate) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--sp-3)',
        alignItems: 'center',
      }}
    >
      {canSaveAsTemplate && <SaveAsTemplatePill onClick={onSaveAsTemplate} />}
      {inlineTemplates.map((t) => (
        <TemplatePill
          key={t.id}
          template={t}
          alreadyApplied={appliedIds.has(t.id)}
          onPreview={() => onPreview(t.id)}
          onApply={() => onApply(t.id, t.name)}
        />
      ))}
      {(hasOverflow || isSearching) && (
        <OverflowSearchPill
          open={overflowOpen}
          onToggle={() => setOverflowOpen((o) => !o)}
          onClose={() => setOverflowOpen(false)}
          search={search}
          onSearchChange={onSearchChange}
          templates={templates}
          appliedIds={appliedIds}
          onPreview={onPreview}
          onApply={onApply}
        />
      )}
    </div>
  );
}

// Pill-styled trigger that saves the current prescription draft as a new
// reusable template. Stays consistent with the template pill proportions
// (Add Diagnosis / Add Treatment style).
function SaveAsTemplatePill({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Save current prescription as a new template"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 12px 4px 8px',
        borderRadius: 'var(--radius-full)',
        border: '1px dashed var(--text-muted)',
        background: 'transparent',
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        cursor: 'pointer',
        fontFamily: 'inherit',
        lineHeight: 1.3,
      }}
    >
      <NavIcon name="plus" />
      Save as Template
    </button>
  );
}

// "…" overflow pill: opens a search popover anchored to the pill itself.
// Lets the user search the full template list without cluttering the inline
// row when there are many templates.
function OverflowSearchPill({
  open,
  onToggle,
  onClose,
  search,
  onSearchChange,
  templates,
  appliedIds,
  onPreview,
  onApply,
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  search: string;
  onSearchChange: (v: string) => void;
  templates: PrescriptionTemplateListRow[];
  appliedIds: Set<string>;
  onPreview: (id: string) => void;
  onApply: (id: string, name: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        title="Search more templates"
        style={{
          padding: '10px 14px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border)',
          background: open ? 'var(--bg-surface-2, #f1f5f9)' : 'var(--bg-surface)',
          color: 'var(--text-muted)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semi)',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        …
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            width: 320,
            maxWidth: 'calc(100vw - 48px)',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            padding: 'var(--sp-3)',
            zIndex: 30,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
          }}
        >
          <div style={{ position: 'relative' }}>
            <input
              autoFocus
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search prescription templates..."
              style={{
                width: '100%',
                padding: '6px 10px 6px 32px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                fontSize: 'var(--font-size-xs)',
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
            <span
              style={{
                position: 'absolute',
                top: 6,
                left: 10,
                color: 'var(--text-muted)',
                pointerEvents: 'none',
              }}
            >
              <NavIcon name="search" />
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              maxHeight: 280,
              overflowY: 'auto',
            }}
          >
            {templates.length === 0 ? (
              <div
                style={{
                  padding: 'var(--sp-2)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                }}
              >
                No templates found.
              </div>
            ) : (
              templates.map((t) => (
                <TemplatePill
                  key={t.id}
                  template={t}
                  alreadyApplied={appliedIds.has(t.id)}
                  onPreview={() => onPreview(t.id)}
                  onApply={() => {
                    onApply(t.id, t.name);
                    onClose();
                  }}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Pastel tone palette for template pills — each template gets a stable tone
// based on a hash of its id so doctors can recognize repeats by color. Kept
// in-sync visually with the App Launcher tone family.
const PILL_TONES = [
  '#4f46e5', // indigo
  '#0284c7', // sky
  '#0e7490', // cyan
  '#0f766e', // teal
  '#059669', // emerald
  '#b45309', // amber
  '#c2410c', // orange
  '#e11d48', // rose
  '#be185d', // pink
  '#7c3aed', // violet
  '#9333ea', // purple
] as const;

function toneForTemplateId(id: string): string {
  // Cheap stable hash — sum of char codes is enough for ~11 buckets.
  let sum = 0;
  for (let i = 0; i < id.length; i++) sum += id.charCodeAt(i);
  return PILL_TONES[sum % PILL_TONES.length];
}

// Pill-shaped template button: clicking the body applies the template,
// clicking the (ⓘ) icon opens the preview drawer. Left accent border uses a
// per-template pastel tone; applied templates get a thicker accent strip.
function TemplatePill({
  template,
  alreadyApplied,
  onPreview,
  onApply,
}: {
  template: PrescriptionTemplateListRow;
  alreadyApplied: boolean;
  onPreview: () => void;
  onApply: () => void;
}) {
  const tone = toneForTemplateId(template.id);
  return (
    <div
      title={alreadyApplied ? 'Already applied to this draft' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        // Matches the Add Diagnosis / Add Treatment chip-pill proportions
        // (radius-full, xs font, medium weight, ~4px vertical padding).
        borderRadius: 'var(--radius-full)',
        border: `1px solid ${tone}`,
        background: 'var(--bg-surface)',
        color: tone,
        // Slightly heavier when already applied — keeps the same height by
        // using an inset shadow instead of a thicker border.
        boxShadow: alreadyApplied ? `inset 0 0 0 1px ${tone}` : undefined,
        lineHeight: 1.3,
      }}
    >
      <button
        type="button"
        onClick={onApply}
        title={alreadyApplied ? 'Apply this template again' : 'Apply template'}
        style={{
          padding: '4px 8px 4px 12px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-medium)',
          color: tone,
          whiteSpace: 'nowrap',
          lineHeight: 1.3,
        }}
      >
        {template.name}
      </button>
      <button
        type="button"
        onClick={onPreview}
        title="Preview template"
        aria-label={`Preview ${template.name}`}
        style={{
          width: 18,
          height: 18,
          marginRight: 6,
          flexShrink: 0,
          border: 'none',
          borderRadius: 'var(--radius-full)',
          background: 'transparent',
          color: tone,
          opacity: 0.7,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        <InfoCircleIcon />
      </button>
    </div>
  );
}

function InfoCircleIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

// ─── PRD.219 — Template preview drawer ───

function TemplatePreviewDrawer({
  templateId,
  onClose,
  onApply,
  primary,
}: {
  templateId: string | null;
  onClose: () => void;
  onApply: (id: string, name: string) => void;
  primary: string;
}) {
  // Drawer mounts queries lazily by using empty templateId until opened.
  const { data: templateRows = [] } = usePowerSyncQuery<PrescriptionTemplateRow>(
    qPrescriptionTemplateById(templateId ?? ''),
  );
  const { data: itemRows = [] } = usePowerSyncQuery<PrescriptionTemplateItemRow>(
    qPrescriptionTemplateItems(templateId ?? ''),
  );
  const template = templateId ? (templateRows[0] ?? null) : null;

  // While the row hasn't loaded yet, show a minimal placeholder title.
  const title = template?.name ?? (templateId ? 'Loading template…' : 'Template Preview');

  return (
    <RightDrawer
      open={templateId !== null}
      onClose={onClose}
      title={title}
      icon="clipboard"
      expandable
      expandedWidth="min(960px, 85vw)"
      collapsedWidth={520}
    >
      {!template ? (
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          {templateId ? 'Could not load this template. Please try again.' : ''}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          {/* Header meta */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
              }}
            >
              {[template.category, template.specialty].filter(Boolean).join(' · ') || '—'}
            </div>
            {template.description && (
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
                {template.description}
              </div>
            )}
          </div>

          {template.default_instructions && (
            <div
              style={{
                padding: 'var(--sp-3)',
                background: 'var(--bg-surface-2, #fafafa)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  marginBottom: 4,
                }}
              >
                Default Instructions
              </div>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
                {template.default_instructions}
              </div>
            </div>
          )}

          {/* Medicine rows */}
          <div>
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                marginBottom: 6,
              }}
            >
              Medicines ({itemRows.length})
            </div>
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
              }}
            >
              {itemRows.map((it, idx) => (
                <PreviewItemRow key={it.id} item={it} index={idx} />
              ))}
              {itemRows.length === 0 && (
                <div
                  style={{
                    padding: 'var(--sp-3)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                    textAlign: 'center',
                  }}
                >
                  No medicines on this template.
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 14px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => onApply(template.id, template.name)}
              disabled={itemRows.length === 0}
              style={{
                padding: '8px 14px',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: primary,
                color: '#fff',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: itemRows.length === 0 ? 'not-allowed' : 'pointer',
                opacity: itemRows.length === 0 ? 0.5 : 1,
              }}
            >
              Apply Template
            </button>
          </div>
        </div>
      )}
    </RightDrawer>
  );
}

function PreviewItemRow({ item, index }: { item: PrescriptionTemplateItemRow; index: number }) {
  const scheduleParts = [
    item.schedule_morning === 1 && 'M',
    item.schedule_afternoon === 1 && 'A',
    item.schedule_night === 1 && 'N',
  ].filter(Boolean) as string[];
  const durationLabel =
    item.duration_value != null && item.duration_unit
      ? `${item.duration_value} ${item.duration_unit}`
      : null;
  return (
    <div
      style={{
        padding: 'var(--sp-3)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--text)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            minWidth: 20,
          }}
        >
          {index + 1}.
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>{item.item_name}</span>
        {item.inventory_item_id ? (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-success-bg, #dcfce7)',
              color: 'var(--color-success-text, #166534)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Inventory-linked
          </span>
        ) : (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--bg-surface-2, #f1f5f9)',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Free-text
          </span>
        )}
      </div>
      {item.item_details && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {item.item_details}
        </div>
      )}
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        {item.intake_timing && <span>When: {item.intake_timing.replace('_', ' ')}</span>}
        {item.dose_quantity && <span>Dose: {item.dose_quantity}</span>}
        {scheduleParts.length > 0 && <span>Schedule: {scheduleParts.join('/')}</span>}
        {durationLabel && <span>Duration: {durationLabel}</span>}
      </div>
      {item.instructions && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text)' }}>
          {item.instructions}
        </div>
      )}
    </div>
  );
}

// ─── Save-as-Template modal ───
//
// Captures the metadata needed to insert a new prescription_templates row.
// The actual prescription_template_items rows are derived from the current
// encounter draft by the parent and written in the same handler.

interface SaveAsTemplateInput {
  name: string;
  description: string;
  category: string;
  specialty: string;
  defaultInstructions: string;
  isActive: boolean;
}

function SaveAsTemplateModal({
  itemCount,
  saving,
  onCancel,
  onSave,
  primary,
}: {
  itemCount: number;
  saving: boolean;
  onCancel: () => void;
  onSave: (input: SaveAsTemplateInput) => void;
  primary: string;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [defaultInstructions, setDefaultInstructions] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [nameError, setNameError] = useState<string | null>(null);

  // Close on Escape.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !saving) onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel, saving]);

  const handleSubmit = () => {
    if (!name.trim()) {
      setNameError('Template name is required.');
      return;
    }
    onSave({
      name,
      description,
      category,
      specialty,
      defaultInstructions,
      isActive,
    });
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    color: 'var(--text)',
    fontSize: 'var(--font-size-sm)',
    outline: 'none',
    fontFamily: 'inherit',
  };

  return (
    <>
      <div
        onClick={() => !saving && onCancel()}
        style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.32)', zIndex: 9000 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Save current prescription as a new template"
        className="pc-viewport-clamp"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(520px, calc(100vw - 32px))',
          overflowY: 'auto',
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
          zIndex: 9001,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: 'var(--sp-5)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 'var(--sp-3)',
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Save as Template
            </h2>
            <p
              style={{
                margin: '4px 0 0',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
              }}
            >
              Saves the current {itemCount} medicine row{itemCount === 1 ? '' : 's'} as a reusable
              prescription template.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !saving && onCancel()}
            aria-label="Close"
            style={{
              width: 28,
              height: 28,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg-surface)',
              color: 'var(--text-muted)',
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              flexShrink: 0,
            }}
          >
            <NavIcon name="x" />
          </button>
        </div>

        <div
          style={{
            padding: 'var(--sp-5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-4)',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={labelStyle}>
              Template Name <span style={{ color: 'var(--color-danger-text, #e53e3e)' }}>*</span>
            </span>
            <input
              autoFocus
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
              placeholder="e.g. Upper Respiratory Infection"
              style={inputStyle}
            />
            {nameError && (
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-danger-text, #e53e3e)',
                }}
              >
                {nameError}
              </span>
            )}
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={labelStyle}>Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Short description of when to use this template (optional)."
              style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
            />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={labelStyle}>Category</span>
              <input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. URI, Dental"
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={labelStyle}>Specialty</span>
              <input
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                placeholder="e.g. General, Dermatology"
                style={inputStyle}
              />
            </label>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={labelStyle}>Default Instructions</span>
            <textarea
              value={defaultInstructions}
              onChange={(e) => setDefaultInstructions(e.target.value)}
              rows={3}
              placeholder="Patient-facing instructions, e.g. Increase oral fluid intake."
              style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
            />
          </label>

          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              style={{ accentColor: primary }}
            />
            Save as Active
          </label>
        </div>

        <div
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--sp-2)',
          }}
        >
          <button
            type="button"
            onClick={() => !saving && onCancel()}
            disabled={saving}
            style={{
              padding: '8px 14px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 500,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            style={{
              padding: '8px 14px',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: primary,
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save Template'}
          </button>
        </div>
      </div>
    </>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  color: 'var(--text-muted)',
};

// ─── Shared-grid table-inspired editor ───

const GRID_TEMPLATE =
  '36px minmax(160px, 1.4fr) minmax(180px, 1.6fr) 130px 90px 132px 154px minmax(180px, 1.6fr) 84px';
const GRID_MIN_WIDTH = 1146;

interface PrescriptionGridProps {
  drafts: PrescriptionDraft[];
  dirtyIds: Set<string>;
  savingId: string | null;
  primary: string;
  /** Allergy conflicts per line id (empty/absent = no conflict). */
  conflictsByDraftId: Map<string, AllergyConflict[]>;
  onChange: (id: string, patch: Partial<PrescriptionDraft>) => void;
  onSave: (draft: PrescriptionDraft) => void;
  onRemove: (draft: PrescriptionDraft) => void;
  /** PRD.217 §12 — promote an AI draft prescription to status='final'. */
  onAccept: (draft: PrescriptionDraft) => void;
}

function PrescriptionGrid({
  drafts,
  dirtyIds,
  savingId,
  primary,
  conflictsByDraftId,
  onChange,
  onSave,
  onRemove,
  onAccept,
}: PrescriptionGridProps) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-surface)',
        overflowX: 'auto',
      }}
    >
      <div
        role="table"
        style={{
          display: 'grid',
          gridTemplateColumns: GRID_TEMPLATE,
          columnGap: 0,
          rowGap: 0,
          minWidth: GRID_MIN_WIDTH,
          alignItems: 'center',
        }}
      >
        {/* Header */}
        <HeaderCell align="center">#</HeaderCell>
        <HeaderCell>Item Name</HeaderCell>
        <HeaderCell>Strength / Dose</HeaderCell>
        <HeaderCell>When to take</HeaderCell>
        <HeaderCell>Qty</HeaderCell>
        <HeaderCell>Schedule</HeaderCell>
        <HeaderCell>Duration</HeaderCell>
        <HeaderCell>Instructions</HeaderCell>
        <HeaderCell align="right">Actions</HeaderCell>

        {/* Rows */}
        {drafts.map((draft, idx) => (
          <PrescriptionGridRow
            key={draft.id}
            index={idx}
            draft={draft}
            dirty={dirtyIds.has(draft.id)}
            saving={savingId === draft.id}
            isLast={idx === drafts.length - 1}
            primary={primary}
            conflicts={conflictsByDraftId.get(draft.id) ?? null}
            onChange={(patch) => onChange(draft.id, patch)}
            onSave={() => onSave(draft)}
            onRemove={() => onRemove(draft)}
            onAccept={() => onAccept(draft)}
          />
        ))}
      </div>
    </div>
  );
}

function HeaderCell({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <div
      style={{
        padding: '10px 8px',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #fafafa)',
        textAlign: align,
        position: 'sticky',
        top: 0,
        zIndex: 1,
      }}
    >
      {children}
    </div>
  );
}

// ─── Grid row (cells emitted directly into parent grid) ───

interface PrescriptionGridRowProps {
  index: number;
  draft: PrescriptionDraft;
  dirty: boolean;
  saving: boolean;
  isLast: boolean;
  primary: string;
  /** Allergy conflicts for this line, most-severe-first, or null. */
  conflicts: AllergyConflict[] | null;
  onChange: (patch: Partial<PrescriptionDraft>) => void;
  onSave: () => void;
  onRemove: () => void;
  onAccept: () => void;
}

function PrescriptionGridRow({
  index,
  draft,
  dirty,
  saving,
  isLast,
  primary,
  conflicts,
  onChange,
  onSave,
  onRemove,
  onAccept,
}: PrescriptionGridRowProps) {
  const canSave = draft.itemName.trim().length > 0 && dirty && !saving;
  // PRD.217 §12 — AI-suggested draft rows get visual differentiation:
  // a soft tinted background + the inline AI Draft badge in the Item Name
  // cell. Promotes via the Accept button in the Actions cell.
  const isDraft = draft.status === 'draft';
  const isAi = draft.source === 'ai';
  // Allergy conflict takes visual precedence: a red/amber row tint keyed to the
  // worst severity overrides the draft tint so the risk is unmissable.
  const hasConflict = !!conflicts && conflicts.length > 0;
  const conflictTone = hasConflict ? allergyTone(highestSeverity(conflicts!)) : null;
  const cellStyle: React.CSSProperties = {
    padding: '8px 8px',
    borderBottom: isLast ? 'none' : '1px solid var(--border)',
    minHeight: 56,
    display: 'flex',
    alignItems: 'center',
    background: conflictTone ? conflictTone.rowBg : isDraft ? `${primary}07` : undefined,
  };
  const inputStyle: React.CSSProperties = {
    height: 32,
    padding: '0 8px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--text)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <>
      {/* # / Index */}
      <div
        style={{
          ...cellStyle,
          justifyContent: 'center',
          color: 'var(--text-muted)',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          gap: 4,
        }}
      >
        {index + 1}
        {draft.isNew && (
          <span
            title="Unsaved"
            aria-label="Unsaved"
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: primary,
              display: 'inline-block',
            }}
          />
        )}
      </div>

      {/* Item Name (with inventory picker) */}
      <div style={{ ...cellStyle, flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
        {(isDraft || isAi || hasConflict) && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {hasConflict && <AllergyPill conflicts={conflicts!} />}
            {isAi && <AiSuggestedPill primary={primary} />}
            {isDraft && <DraftPill />}
          </div>
        )}
        <ItemNamePicker
          value={draft.itemName}
          inventoryItemId={draft.inventoryItemId}
          unresolved={isAi && !draft.inventoryItemId}
          onTypeFreeText={(text) =>
            // Typing a name in free-text mode leaves any existing
            // inventory binding intact only while the typed text matches
            // the snapshot — diverging text un-binds the inventory item
            // so the snapshot is not silently misrepresenting the line.
            onChange(
              draft.inventoryItemId && text !== draft.itemNameSnapshot
                ? { ...clearInventoryBinding(draft), itemName: text }
                : { itemName: text, freeTextName: draft.inventoryItemId ? null : text || null },
            )
          }
          onSelectInventory={(selection) => {
            const next = applyPickerSelection(draft, selection);
            onChange({
              itemName: next.itemName,
              itemDetails: next.itemDetails,
              doseQuantity: next.doseQuantity,
              instructions: next.instructions,
              inventoryItemId: next.inventoryItemId,
              freeTextName: next.freeTextName,
              itemNameSnapshot: next.itemNameSnapshot,
              skuSnapshot: next.skuSnapshot,
              genericNameSnapshot: next.genericNameSnapshot,
              brandNameSnapshot: next.brandNameSnapshot,
              strengthSnapshot: next.strengthSnapshot,
              dosageFormSnapshot: next.dosageFormSnapshot,
              routeSnapshot: next.routeSnapshot,
              unitSnapshot: next.unitSnapshot,
              defaultInstructionSnapshot: next.defaultInstructionSnapshot,
              defaultDosageSnapshot: next.defaultDosageSnapshot,
              usageSnapshot: next.usageSnapshot,
              doseSnapshot: next.doseSnapshot,
              doseUnitSnapshot: next.doseUnitSnapshot,
              frequencySnapshot: next.frequencySnapshot,
              durationSnapshot: next.durationSnapshot,
            });
          }}
          onClearInventory={() => {
            const next = clearInventoryBinding(draft);
            onChange({
              inventoryItemId: next.inventoryItemId,
              freeTextName: next.freeTextName,
              itemNameSnapshot: next.itemNameSnapshot,
              skuSnapshot: next.skuSnapshot,
              genericNameSnapshot: next.genericNameSnapshot,
              brandNameSnapshot: next.brandNameSnapshot,
              strengthSnapshot: next.strengthSnapshot,
              dosageFormSnapshot: next.dosageFormSnapshot,
              routeSnapshot: next.routeSnapshot,
              unitSnapshot: next.unitSnapshot,
              defaultInstructionSnapshot: next.defaultInstructionSnapshot,
              defaultDosageSnapshot: next.defaultDosageSnapshot,
              usageSnapshot: next.usageSnapshot,
              doseSnapshot: next.doseSnapshot,
              doseUnitSnapshot: next.doseUnitSnapshot,
              frequencySnapshot: next.frequencySnapshot,
              durationSnapshot: next.durationSnapshot,
            });
          }}
          inputStyle={inputStyle}
          primary={primary}
        />
      </div>

      {/* Strength / Dose */}
      <div style={cellStyle}>
        <input
          type="text"
          placeholder="Brand, strength, formulation"
          value={draft.itemDetails}
          onChange={(e) => onChange({ itemDetails: e.target.value })}
          style={inputStyle}
        />
      </div>

      {/* When to take */}
      <div style={cellStyle}>
        <select
          value={draft.intakeTiming}
          onChange={(e) => onChange({ intakeTiming: e.target.value })}
          style={inputStyle}
        >
          {INTAKE_TIMING_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Dose */}
      <div style={cellStyle}>
        <input
          type="text"
          placeholder="1 tab"
          value={draft.doseQuantity}
          onChange={(e) => onChange({ doseQuantity: e.target.value })}
          style={inputStyle}
        />
      </div>

      {/* Schedule (M / A / N) */}
      <div style={cellStyle}>
        <div style={{ display: 'flex', gap: 4 }}>
          <ScheduleChip
            short="M"
            full="Morning"
            active={draft.scheduleMorning}
            primary={primary}
            onClick={() => onChange({ scheduleMorning: !draft.scheduleMorning })}
          />
          <ScheduleChip
            short="A"
            full="Afternoon"
            active={draft.scheduleAfternoon}
            primary={primary}
            onClick={() => onChange({ scheduleAfternoon: !draft.scheduleAfternoon })}
          />
          <ScheduleChip
            short="N"
            full="Night"
            active={draft.scheduleNight}
            primary={primary}
            onClick={() => onChange({ scheduleNight: !draft.scheduleNight })}
          />
        </div>
      </div>

      {/* Duration */}
      <div style={cellStyle}>
        <div style={{ display: 'flex', gap: 4, width: '100%' }}>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="5"
            value={draft.durationValue}
            onChange={(e) => onChange({ durationValue: e.target.value })}
            style={{ ...inputStyle, width: 56, padding: '0 6px' }}
          />
          <select
            value={draft.durationUnit}
            onChange={(e) => onChange({ durationUnit: e.target.value })}
            style={{ ...inputStyle, flex: 1, padding: '0 4px' }}
          >
            {DURATION_UNIT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Instructions */}
      <div style={cellStyle}>
        <input
          type="text"
          placeholder="e.g. Take in lukewarm water"
          value={draft.instructions}
          onChange={(e) => onChange({ instructions: e.target.value })}
          style={inputStyle}
        />
      </div>

      {/* Actions */}
      <div
        style={{
          ...cellStyle,
          justifyContent: 'flex-end',
          gap: 4,
        }}
      >
        {/* PRD.217 §12 — Accept promotes an AI draft to a final prescription.
            Only shown for persisted draft rows; new clinician-typed rows are
            saved-then-final via the existing Save flow. */}
        {isDraft && !draft.isNew && (
          <button
            type="button"
            onClick={onAccept}
            disabled={saving}
            title="Accept — promote this draft to a final prescription"
            style={{
              height: 30,
              padding: '0 10px',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: primary,
              color: '#fff',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <NavIcon name="check" />
            Accept
          </button>
        )}
        {dirty && (
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave}
            title={draft.isNew ? 'Save item' : 'Update item'}
            style={{
              height: 30,
              width: 30,
              padding: 0,
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: !canSave ? 'var(--text-disabled)' : primary,
              color: '#fff',
              cursor: !canSave ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <NavIcon name={saving ? 'loader' : 'check'} />
          </button>
        )}
        <button
          type="button"
          onClick={onRemove}
          title="Remove item"
          style={{
            height: 30,
            width: 30,
            padding: 0,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <NavIcon name="x" />
        </button>
      </div>
    </>
  );
}

// ─── PRD.217 row pills ───

function AiSuggestedPill({ primary }: { primary: string }) {
  return (
    <span
      title="Auto-suggested by the dermatology AI run"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        height: 18,
        padding: '0 7px',
        borderRadius: 'var(--radius-full)',
        background: primary,
        color: '#fff',
        fontSize: 10,
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: 0.1,
      }}
    >
      <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2z" />
      </svg>
      AI Suggested
    </span>
  );
}

function DraftPill() {
  return (
    <span
      title="Draft — not included in the printed prescription. Click Accept to promote."
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 18,
        padding: '0 7px',
        borderRadius: 'var(--radius-full)',
        background: '#fef3c7',
        color: '#92400e',
        fontSize: 10,
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: 0.1,
      }}
    >
      Draft
    </span>
  );
}

// ─── Allergy safety UI ───

interface AllergyTone {
  rowBg: string;
  pillBg: string;
  pillText: string;
  border: string;
  bannerBg: string;
  bannerText: string;
}

/** Severe conflicts read as danger (red); everything else as warning (amber). */
function allergyTone(severity: AllergySeverity): AllergyTone {
  if (severity === 'severe') {
    return {
      rowBg: 'var(--color-danger-bg, #fef2f2)',
      pillBg: 'var(--color-danger-text, #dc2626)',
      pillText: '#fff',
      border: 'var(--color-danger-border, #fecaca)',
      bannerBg: 'var(--color-danger-bg, #fef2f2)',
      bannerText: 'var(--color-danger-text, #b91c1c)',
    };
  }
  return {
    rowBg: 'var(--color-warning-bg, #fffbeb)',
    pillBg: '#d97706',
    pillText: '#fff',
    border: 'var(--color-warning-border, #fde68a)',
    bannerBg: 'var(--color-warning-bg, #fffbeb)',
    bannerText: 'var(--color-warning-text, #92400e)',
  };
}

function WarnTriangle({ size = 11 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2L1 21h22L12 2zm0 6.5c.6 0 1 .4 1 1v4a1 1 0 11-2 0v-4c0-.6.4-1 1-1zm0 9.5a1.2 1.2 0 110-2.4 1.2 1.2 0 010 2.4z" />
    </svg>
  );
}

function describeConflict(c: AllergyConflict): string {
  const sev = c.severity !== 'unknown' ? ` — ${c.severity}` : '';
  const reaction = c.reaction ? ` (${c.reaction})` : '';
  return `${c.substance}${sev}${reaction}`;
}

/** Inline pill on a prescription line that matches a documented allergy. */
function AllergyPill({ conflicts }: { conflicts: AllergyConflict[] }) {
  const tone = allergyTone(highestSeverity(conflicts));
  const title = `Patient allergy: ${conflicts.map(describeConflict).join('; ')}`;
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        height: 18,
        padding: '0 7px',
        borderRadius: 'var(--radius-full)',
        background: tone.pillBg,
        color: tone.pillText,
        fontSize: 10,
        fontWeight: 700,
        lineHeight: 1.4,
        letterSpacing: 0.2,
        textTransform: 'uppercase',
      }}
    >
      <WarnTriangle size={9} />
      Allergy
    </span>
  );
}

/** Summary banner above the grid listing every conflicting substance. */
// PRD.280 — recording / processing / failure panel for voice prescription.
// Red is used ONLY for the active recording indicator + Stop. Processing phases
// announce sequential status via aria-live; failures preserve manual entry.
function VoicePrescriptionPanel({
  voice,
  primary,
}: {
  voice: UseVoicePrescriptionReturn;
  primary: string;
}) {
  const { phase } = voice;
  if (phase === 'idle') return null;

  const fmt = (s: number) => {
    const mm = Math.floor(s / 60)
      .toString()
      .padStart(2, '0');
    const ss = Math.floor(s % 60)
      .toString()
      .padStart(2, '0');
    return `${mm}:${ss}`;
  };

  if (phase === 'recording') {
    const pct = Math.min(100, Math.round((voice.elapsed / voice.maxDuration) * 100));
    const nearMax = voice.elapsed >= voice.maxDuration - 10;
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          padding: '10px 12px',
          borderRadius: 'var(--radius-md)',
          border: `1px solid ${nearMax ? '#dc2626' : 'var(--border)'}`,
          background: nearMax ? '#fef2f2' : 'var(--bg-surface)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            aria-hidden
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: '#dc2626',
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--text)' }}>
            Listening…
          </span>
          <span
            style={{
              marginLeft: 'auto',
              fontVariantNumeric: 'tabular-nums',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {fmt(voice.elapsed)} / {fmt(voice.maxDuration)}
          </span>
        </div>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={voice.maxDuration}
          aria-valuenow={voice.elapsed}
          style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}
        >
          <div style={{ width: `${pct}%`, height: '100%', background: '#dc2626' }} />
        </div>
        {/* PRD.280 — accuracy is highest in English; non-English speech can miss
            some details. Surfaced during recording so the clinician reviews items. */}
        <div
          role="note"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 6,
            padding: '6px 8px',
            borderRadius: 'var(--radius-sm)',
            background: '#fffbeb',
            border: '1px solid #fde68a',
            color: '#92400e',
            fontSize: 'var(--font-size-xs)',
            lineHeight: 1.4,
          }}
        >
          <NavIcon name="alert-triangle" />
          <span>
            Works best in English. Non-English speech may be less accurate and can miss some details
            — review the added items before prescribing.
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={voice.stop}
            style={{
              height: 32,
              padding: '0 14px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: '#dc2626',
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Stop
          </button>
          <button
            type="button"
            onClick={voice.cancel}
            style={{
              height: 32,
              padding: '0 14px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div
        role="alert"
        style={{
          padding: '10px 12px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border)',
          background: '#fef3c7',
          color: '#92400e',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
          {voice.error ?? 'Could not extract prescription items.'}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {voice.retryable && (
            <button
              type="button"
              onClick={voice.start}
              style={{
                height: 30,
                padding: '0 12px',
                borderRadius: 'var(--radius-md)',
                border: `1px solid ${primary}`,
                background: 'var(--bg-surface)',
                color: primary,
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
          )}
          <button
            type="button"
            onClick={voice.dismissError}
            style={{
              height: 30,
              padding: '0 12px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Add manually
          </button>
        </div>
      </div>
    );
  }

  // Processing: transcribing → extracting → adding.
  const message =
    phase === 'transcribing'
      ? 'Transcribing and translating…'
      : phase === 'extracting'
        ? 'Extracting medications…'
        : 'Adding prescription items…';
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        padding: '10px 12px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          border: `2px solid ${primary}`,
          borderTopColor: 'transparent',
          animation: 'spin 0.8s linear infinite',
          flexShrink: 0,
        }}
      />
      <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--text)' }}>
        {message}
      </span>
    </div>
  );
}

function AllergyConflictBanner({
  lineCount,
  items,
}: {
  lineCount: number;
  items: { substance: string; severity: AllergySeverity }[];
}) {
  const tone = allergyTone(items.some((i) => i.severity === 'severe') ? 'severe' : 'moderate');
  const label = items
    .map((i) => (i.severity !== 'unknown' ? `${i.substance} (${i.severity})` : i.substance))
    .join(', ');
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '10px 12px',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${tone.border}`,
        background: tone.bannerBg,
        color: tone.bannerText,
        fontSize: 'var(--font-size-sm)',
      }}
    >
      <span style={{ display: 'flex', marginTop: 1, flexShrink: 0 }}>
        <WarnTriangle size={14} />
      </span>
      <span>
        <strong>
          {lineCount} prescribed item{lineCount > 1 ? 's' : ''} conflict
          {lineCount > 1 ? '' : 's'} with a known allergy:
        </strong>{' '}
        {label}. Review before prescribing.
      </span>
    </div>
  );
}

/** Soft-stop confirmation requiring a recorded reason to override an allergy. */
function AllergyOverrideModal({
  medicineName,
  conflicts,
  onCancel,
  onConfirm,
}: {
  medicineName: string;
  conflicts: AllergyConflict[];
  onCancel: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const tone = allergyTone(highestSeverity(conflicts));
  const canConfirm = reason.trim().length > 0 && !submitting;

  const submit = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      await onConfirm(reason);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <>
      <div
        onClick={onCancel}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1400 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Allergy warning"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 440,
          maxWidth: '92vw',
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          border: `1px solid ${tone.border}`,
          boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
          zIndex: 1401,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: 'var(--sp-4) var(--sp-5)',
            background: tone.bannerBg,
            color: tone.bannerText,
            borderBottom: `1px solid ${tone.border}`,
          }}
        >
          <WarnTriangle size={16} />
          <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 700 }}>
            Allergy warning
          </span>
        </div>

        <div style={{ padding: 'var(--sp-5)' }}>
          <p style={{ margin: '0 0 10px', fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
            <strong>{medicineName}</strong> conflicts with a documented allergy for this patient:
          </p>
          <ul
            style={{
              margin: '0 0 14px',
              paddingLeft: 18,
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {conflicts.map((c) => (
              <li key={c.substance}>{describeConflict(c)}</li>
            ))}
          </ul>

          <label
            style={{
              display: 'block',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text-muted)',
              marginBottom: 6,
            }}
          >
            Reason for prescribing anyway (required)
          </label>
          <textarea
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Prior tolerance confirmed; benefit outweighs risk; patient counselled."
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '8px 10px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              outline: 'none',
              resize: 'vertical',
            }}
          />

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 'var(--sp-4)',
            }}
          >
            <button
              type="button"
              onClick={onCancel}
              style={{
                height: 34,
                padding: '0 14px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canConfirm}
              title={!reason.trim() ? 'Enter a reason to continue' : undefined}
              style={{
                height: 34,
                padding: '0 14px',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: canConfirm ? tone.pillBg : 'var(--text-disabled, #cbd5e1)',
                color: '#fff',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 700,
                cursor: canConfirm ? 'pointer' : 'not-allowed',
              }}
            >
              {submitting ? 'Saving…' : 'Prescribe anyway'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

// ─── Schedule chip ───

function ScheduleChip({
  short,
  full,
  active,
  primary,
  onClick,
}: {
  short: string;
  full: string;
  active: boolean;
  primary: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={full}
      aria-label={full}
      style={{
        width: 34,
        height: 34,
        padding: 0,
        borderRadius: 'var(--radius-sm)',
        border: `1px solid ${active ? primary : 'var(--border)'}`,
        background: active ? primary : 'var(--bg-surface)',
        color: active ? '#fff' : 'var(--text)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semi)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {short}
    </button>
  );
}

// ─── Item Name Picker (combobox: free text + Medicine inventory dropdown) ───

/**
 * Inline combobox that lets the prescriber either type a medicine name
 * (free-text mode) or pick a Medicine item from inventory. Selection
 * triggers `onSelectInventory` with the picker selection; pure typing
 * triggers `onTypeFreeText`. When bound to an inventory item, an "×"
 * chip lets the prescriber clear the binding.
 *
 * Backed by `qInventoryPickerCandidates` (PRD.188). Falls back gracefully
 * if the inventory query is empty — free text always remains available.
 */
function ItemNamePicker({
  value,
  inventoryItemId,
  onTypeFreeText,
  onSelectInventory,
  onClearInventory,
  inputStyle,
  primary,
  unresolved,
}: {
  value: string;
  inventoryItemId: string | null;
  onTypeFreeText: (text: string) => void;
  onSelectInventory: (selection: InventoryPickerSelection) => void;
  onClearInventory: () => void;
  inputStyle: React.CSSProperties;
  primary: string;
  // PRD.280 — true for an AI voice row with no inventory link. Surfaces the
  // `Match inventory` affordance; the search is prefilled with the extracted
  // medicine name (the input `value`).
  unresolved?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // PRD.198 fix — the dropdown is portaled to document.body so it escapes
  // the prescription grid's `overflowX: auto` clipping context. Position
  // is recomputed from the anchor's bounding rect whenever the dropdown
  // opens or the page scrolls/resizes.
  const [anchorRect, setAnchorRect] = useState<{
    top: number;
    bottom: number;
    left: number;
    width: number;
  } | null>(null);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(value), 200);
    return () => clearTimeout(t);
  }, [value]);

  // Close on outside click — also dismiss when click lands on the
  // portaled dropdown's container outside both anchor and dropdown.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      // The portaled dropdown lives outside containerRef, so check it
      // separately by walking up looking for our role=listbox marker.
      let el: Node | null = target;
      while (el && el !== document.body) {
        if (el instanceof HTMLElement && el.dataset?.pickerDropdown === 'item-name') {
          return;
        }
        el = el.parentNode;
      }
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Track the anchor's screen position while the dropdown is open. We
  // use a layout effect so the dropdown paints in the right place on
  // open without a flicker, and listen for scroll/resize so it follows
  // the input if the user scrolls the encounter view.
  useLayoutEffect(() => {
    if (!open) {
      setAnchorRect(null);
      return;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setAnchorRect({ top: r.top, bottom: r.bottom, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  // PRD.205 — dermatology prescriptions/care plans need Product results
  // alongside Medicine. The picker query now accepts an itemTypes filter;
  // results are split into Medicine and Product sections below so the
  // prescriber can tell them apart.
  const { data: candidateRows = [] } = usePowerSyncQuery<InventoryPickerCandidateRow>(
    qInventoryPickerCandidates(debouncedQuery, ['medicine', 'product']),
  );
  const medicineCandidateRows = useMemo(
    () => candidateRows.filter((r) => r.item_type !== 'product'),
    [candidateRows],
  );
  const productCandidateRows = useMemo(
    () => candidateRows.filter((r) => r.item_type === 'product'),
    [candidateRows],
  );

  // PRD.198 — Manual fallback row appears when the user has typed something
  // that doesn't exactly match an inventory result. Click it to dismiss the
  // dropdown; the typed text is already in the draft via onTypeFreeText.
  const trimmed = value.trim();
  const hasTyped = trimmed.length > 0;
  const exactInventoryMatch = candidateRows.some(
    (r) => (r.item_name ?? '').toLowerCase() === trimmed.toLowerCase(),
  );
  const showMedicineSection = medicineCandidateRows.length > 0;
  const showProductSection = productCandidateRows.length > 0;
  const showInventorySection = candidateRows.length > 0;
  const showManualOption = hasTyped && !exactInventoryMatch;
  const showDropdown = open && (showInventorySection || showManualOption);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div ref={anchorRef} style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
        <input
          ref={inputRef}
          type="text"
          placeholder="Search medicine, product, or type custom item…"
          value={value}
          onChange={(e) => {
            onTypeFreeText(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          style={
            unresolved && !inventoryItemId
              ? { ...inputStyle, borderColor: '#d97706', background: '#fffbeb' }
              : inputStyle
          }
        />
        {inventoryItemId && (
          <span
            title="Linked to inventory item — click × to switch to free text"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              padding: '0 6px',
              height: 22,
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-success-bg, #dcfce7)',
              color: 'var(--color-success-text, #166534)',
              fontSize: 10,
              fontWeight: 600,
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            INV
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClearInventory();
              }}
              aria-label="Unlink inventory item"
              style={{
                marginLeft: 2,
                width: 14,
                height: 14,
                padding: 0,
                borderRadius: 'var(--radius-full)',
                border: 'none',
                background: 'transparent',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: 12,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </span>
        )}
      </div>

      {/* PRD.280 — unresolved AI voice row: visible "Inventory match required"
          state + a keyboard-accessible `Match inventory` action that opens the
          existing picker prefilled with the extracted medicine name. */}
      {unresolved && !inventoryItemId && (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}
        >
          <span style={{ fontSize: 10, fontWeight: 600, color: '#92400e' }} role="note">
            Inventory match required
          </span>
          <button
            type="button"
            onClick={() => {
              setOpen(true);
              inputRef.current?.focus();
            }}
            style={{
              height: 22,
              padding: '0 8px',
              borderRadius: 'var(--radius-full)',
              border: `1px solid ${primary}`,
              background: 'var(--bg-surface)',
              color: primary,
              fontSize: 10,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Match inventory
          </button>
        </div>
      )}

      {showDropdown &&
        anchorRect &&
        createPortal(
          (() => {
            // Flip the dropdown above the input when there isn't enough room
            // below (e.g. the last row near the bottom of the page), and clamp
            // its height to the available space so it never runs off-screen.
            const GAP = 4;
            const MARGIN = 8;
            const MAX_HEIGHT = 320;
            const viewportH = window.innerHeight;
            const spaceBelow = viewportH - anchorRect.bottom - GAP - MARGIN;
            const spaceAbove = anchorRect.top - GAP - MARGIN;
            const placeAbove = spaceBelow < Math.min(MAX_HEIGHT, 240) && spaceAbove > spaceBelow;
            const maxHeight = Math.max(
              120,
              Math.min(MAX_HEIGHT, placeAbove ? spaceAbove : spaceBelow),
            );
            const positionStyle: React.CSSProperties = placeAbove
              ? { bottom: viewportH - anchorRect.top + GAP }
              : { top: anchorRect.bottom + GAP };
            return (
              <div
                role="listbox"
                data-picker-dropdown="item-name"
                style={{
                  position: 'fixed',
                  ...positionStyle,
                  left: anchorRect.left,
                  width: anchorRect.width,
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                  maxHeight,
                  overflowY: 'auto',
                  zIndex: 1000,
                }}
              >
                {showMedicineSection && (
                  <DropdownSectionLabel>Inventory Medicine</DropdownSectionLabel>
                )}
                {showMedicineSection &&
                  medicineCandidateRows.slice(0, 20).map((row) => (
                    <PickerOptionRow
                      key={row.id}
                      row={row}
                      badge="inventory"
                      onSelect={(sel) => {
                        onSelectInventory(sel);
                        setOpen(false);
                      }}
                    />
                  ))}
                {/* PRD.205 — Inventory Product results in dermatology
                prescription/care-plan search. Same write path as Medicine
                (inventory_item_id + snapshots), no stock movement, no
                Pharmacy queue entry. */}
                {showProductSection && (
                  <DropdownSectionLabel>Inventory Product</DropdownSectionLabel>
                )}
                {showProductSection &&
                  productCandidateRows.slice(0, 20).map((row) => (
                    <PickerOptionRow
                      key={row.id}
                      row={row}
                      badge="product"
                      onSelect={(sel) => {
                        onSelectInventory(sel);
                        setOpen(false);
                      }}
                    />
                  ))}
                {showManualOption && <DropdownSectionLabel>Manual</DropdownSectionLabel>}
                {showManualOption && (
                  <button
                    type="button"
                    role="option"
                    onClick={() => setOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 10px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background =
                        'var(--bg-hover, rgba(0,0,0,0.04))';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    }}
                  >
                    <ResultBadge kind="manual" primary={primary} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-semi)',
                          color: 'var(--text)',
                        }}
                      >
                        Use “{trimmed}” as custom medicine
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                          marginTop: 1,
                        }}
                      >
                        Saved as free-text medicine — not linked to inventory.
                      </div>
                    </div>
                  </button>
                )}
              </div>
            );
          })(),
          document.body,
        )}
    </div>
  );
}

function DropdownSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '6px 10px 4px',
        fontSize: 10,
        fontWeight: 'var(--font-weight-semi)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'var(--text-muted)',
        background: 'var(--bg-surface-2, #fafafa)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      {children}
    </div>
  );
}

// PRD.205 — extracted dropdown row so Medicine and Product sections share
// identical visual treatment with only the badge differing. Rendered for
// each candidate row in the prescription/care-plan unified search.
function PickerOptionRow({
  row,
  badge,
  onSelect,
}: {
  row: InventoryPickerCandidateRow;
  badge: 'inventory' | 'product';
  onSelect: (selection: InventoryPickerSelection) => void;
}) {
  const selection = pickerCandidateRowToSelection(row);
  const subtitleParts = [
    selection.genericName && `Generic: ${selection.genericName}`,
    selection.brandName && `Brand: ${selection.brandName}`,
    selection.sku && `SKU: ${selection.sku}`,
  ].filter(Boolean);
  const stockHint =
    selection.trackStock && selection.currentStockDisplay != null
      ? `${selection.currentStockDisplay} ${selection.inventoryUnit ?? ''}`.trim()
      : null;
  return (
    <button
      type="button"
      role="option"
      onClick={() => onSelect(selection)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        width: '100%',
        textAlign: 'left',
        padding: '8px 10px',
        border: 'none',
        borderBottom: '1px solid var(--border)',
        background: 'transparent',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          'var(--bg-hover, rgba(0,0,0,0.04))';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
      }}
    >
      <ResultBadge kind={badge} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          {selection.itemName}
        </div>
        {subtitleParts.length > 0 && (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              marginTop: 1,
            }}
          >
            {subtitleParts.join(' · ')}
          </div>
        )}
        {(selection.dosageForm || selection.route || stockHint) && (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              marginTop: 1,
            }}
          >
            {[selection.dosageForm, selection.route, stockHint && `Stock: ${stockHint}`]
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}
      </div>
    </button>
  );
}

function ResultBadge({
  kind,
  primary,
}: {
  // PRD.205 — `product` joins Medicine and Manual as a labeled result type
  // for dermatology prescription search.
  kind: 'inventory' | 'product' | 'manual';
  primary?: string;
}) {
  const palette =
    kind === 'inventory'
      ? {
          bg: 'var(--color-info-bg, #dbeafe)',
          color: 'var(--color-info-text, #1e40af)',
          label: 'Medicine',
        }
      : kind === 'product'
        ? { bg: '#eef2ff', color: '#4338ca', label: 'Product' }
        : {
            bg: 'var(--color-neutral-bg, #f1f5f9)',
            color: primary || 'var(--text-muted)',
            label: 'Manual',
          };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 6px',
        height: 18,
        borderRadius: 'var(--radius-full)',
        background: palette.bg,
        color: palette.color,
        fontSize: 9,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        flexShrink: 0,
        marginTop: 2,
      }}
    >
      {palette.label}
    </span>
  );
}
